import { useState, useEffect, useMemo, useRef } from 'react';
import { selectTaskDocument } from '@/utils/taskDocumentSelection';
import { X, Play, Loader2, FileText, MessageSquarePlus, CheckCircle2, Upload, Crown, Search, ExternalLink } from 'lucide-react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Input } from './ui/input';
import { ScrollArea } from './ui/scroll-area';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { Task, AVAILABLE_TASKS, TASK_CATEGORY_INFO, TASK_TO_CATEGORY } from '@/types/taskTypes';
import { documentApi, SessionDocument, taskApi, patentApi, userApi } from '@/services/api';
import { OA_EMAIL_TEMPLATES } from '@/config/emailTemplates';
import { useNotificationStore, useSessionStore } from '@/stores';
import { useTaskResultCopy } from '@/hooks/useTaskResultCopy';
import type { TaskResultAttachment } from '@/stores/sessionStore';
import { useAuth } from '@/context/AuthContext';
import { usePremium } from '@/hooks/usePremium';
import { EmailTemplateManager } from '@/components/EmailTemplateManager';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import PartsListDisplay from './task-displays/PartsListDisplay';
import SpecMapDisplay from './task-displays/SpecMapDisplay';
import ClaimsSupportDisplay from './task-displays/ClaimsSupportDisplay';
import ToolModelPicker from './ToolModelPicker';
import ClaimChartDisplay from './task-displays/ClaimChartDisplay';
import ClaimsWithNumeralsDisplay from './task-displays/ClaimsWithNumeralsDisplay';
import CustomAnalysisDisplay from './task-displays/CustomAnalysisDisplay';
import IRMetadataDisplay from './task-displays/IRMetadataDisplay';
import IRInnovationAnalysisDisplay from './task-displays/IRInnovationAnalysisDisplay';
import IRTechClassificationDisplay from './task-displays/IRTechClassificationDisplay';
import DraftSummaryDisplay from './task-displays/DraftSummaryDisplay';
import DraftDetailedDescriptionDisplay from './task-displays/DraftDetailedDescriptionDisplay';
import DraftBackgroundDisplay from './task-displays/DraftBackgroundDisplay';
import DraftClaimsDisplay from './task-displays/DraftClaimsDisplay';
import OtherEmbodimentsDisplay from './task-displays/OtherEmbodimentsDisplay';
import OAExtractDisplay from './task-displays/OAExtractDisplay';
import OAAnalyzeDisplay from './task-displays/OAAnalyzeDisplay';
import OASummaryArtDisplay from './task-displays/OASummaryArtDisplay';
import OATimelineDisplay from './task-displays/OATimelineDisplay';
import TranslateDocumentDisplay from './task-displays/TranslateDocumentDisplay';
import LaTeXConvertDisplay from './task-displays/LaTeXConvertDisplay';
import OCRPdfDisplay from './task-displays/OCRPdfDisplay';
import AntecedentBasisDisplay from './task-displays/AntecedentBasisDisplay';
import TechnicalOutlineDisplay from './task-displays/TechnicalOutlineDisplay';
import PartsProcessOutlineDisplay from './task-displays/PartsProcessOutlineDisplay';
import { OAEmailResultView } from './task-displays/OAEmailResultView';
import PriorArtAnalysisDisplay from './task-displays/PriorArtAnalysisDisplay';
import PatentDrawingDisplay from './task-displays/PatentDrawingDisplay';

/**
 * The tasks that write patent prose, and so honour the account's drafting model.
 *
 * Parts lists, reference numerals and the checks are deliberately absent: their output
 * feeds the numeral allocator, and a model swapped underneath it changes numerals in a
 * drawing that may already be inked.
 */
const DRAFTING_TASK_IDS = new Set([
  'draft_claims',
  'draft_background',
  'draft_summary',
  'draft_detailed_description',
  'draft_other_embodiments',
]);


// Classify a raw patent/publication number entered by the user
function classifyPatentNumber(raw: string): { cleanedNumber: string; docType: string; isApplication: boolean; displayLabel: string } | null {
  if (!raw || !raw.trim()) return null;
  let input = raw.trim();

  // Foreign patent: starts with 2-3 uppercase letters (country code) that are NOT "US"
  const foreignMatch = input.match(/^([A-Z]{2,3})/i);
  if (foreignMatch && foreignMatch[1].toUpperCase() !== 'US') {
    // Keep full string, just remove spaces and commas
    const cleaned = input.replace(/,/g, '').replace(/\s/g, '').toUpperCase();
    const countryCode = foreignMatch[1].toUpperCase();
    return { cleanedNumber: cleaned, docType: 'foreign_patent', isApplication: false, displayLabel: `Foreign Patent (${countryCode}) (${cleaned})` };
  }

  // Strip US prefix, kind code suffix, commas, slashes, spaces
  let cleaned = input
    .replace(/,/g, '')
    .replace(/\//g, '')
    .replace(/\s/g, '')
    .replace(/^US/i, '')
    .replace(/[A-Z]\d*$/i, '');

  if (!/^\d+$/.test(cleaned)) return null;

  // Publication: 10-11 digits starting with year 2xxx
  if ((cleaned.length === 10 || cleaned.length === 11) && /^2\d{3}/.test(cleaned)) {
    // Normalize 10 digits to 11 by inserting 0 after year
    if (cleaned.length === 10) {
      cleaned = cleaned.slice(0, 4) + '0' + cleaned.slice(4);
    }
    return { cleanedNumber: cleaned, docType: 'publication', isApplication: false, displayLabel: `US Publication (${cleaned})` };
  }

  const numVal = parseInt(cleaned, 10);

  // Application: 8 digits, value >= 14,000,000
  if (cleaned.length === 8 && numVal >= 14000000) {
    return { cleanedNumber: cleaned, docType: 'application', isApplication: true, displayLabel: `US Application (${cleaned})` };
  }

  // Patent: 7-8 digits, value < 14,000,000
  if ((cleaned.length === 7 || cleaned.length === 8) && numVal < 14000000) {
    return { cleanedNumber: cleaned, docType: 'patent', isApplication: false, displayLabel: `US Patent (${cleaned})` };
  }

  return null;
}

interface UniversalTaskPanelProps {
  task: Task;
  onClose: () => void;
  preferredDocument?: any;
}

// Language options for translation tasks
const LANGUAGE_OPTIONS = [
  { value: 'auto', label: 'Auto-detect' },
  { value: 'en', label: 'English' },
  { value: 'zh', label: 'Chinese (Simplified)' },
  { value: 'zh-TW', label: 'Chinese (Traditional)' },
  { value: 'ja', label: 'Japanese' },
  { value: 'ko', label: 'Korean' },
  { value: 'de', label: 'German' },
  { value: 'fr', label: 'French' },
  { value: 'es', label: 'Spanish' },
  { value: 'pt', label: 'Portuguese' },
  { value: 'it', label: 'Italian' },
  { value: 'ru', label: 'Russian' },
  { value: 'ar', label: 'Arabic' },
  { value: 'hi', label: 'Hindi' },
  { value: 'nl', label: 'Dutch' },
  { value: 'sv', label: 'Swedish' },
  { value: 'da', label: 'Danish' },
  { value: 'no', label: 'Norwegian' },
  { value: 'fi', label: 'Finnish' },
  { value: 'pl', label: 'Polish' }
];

export function UniversalTaskPanel({ task, onClose, preferredDocument }: UniversalTaskPanelProps) {
  const [textInput, setTextInput] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const processingStartTime = useRef<number | null>(null);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState('');

  // Dynamic form fields
  const [sourceLanguage, setSourceLanguage] = useState('auto');
  const [targetLanguage, setTargetLanguage] = useState('en');
  const [patentNumber, setPatentNumber] = useState('');
  const [applicationNumber, setApplicationNumber] = useState('');
  const [selectedResourceId, setSelectedResourceId] = useState('');
  const [selectedResourceType, setSelectedResourceType] = useState('');
  const [includeSupport, setIncludeSupport] = useState(true);

  // Office Action task-specific fields
  const [selectedApplication, setSelectedApplication] = useState('');
  const [sessionApplications, setSessionApplications] = useState<any[]>([]);
  const [applicationsLoading, setApplicationsLoading] = useState(false);
  const [selectedOfficeAction, setSelectedOfficeAction] = useState('');
  const [prosecutionDocs, setProsecutionDocs] = useState<any[]>([]);
  const [prosecutionDocsLoading, setProsecutionDocsLoading] = useState(false);

  // OA Reporting Email template selection
  const [selectedEmailTemplate, setSelectedEmailTemplate] = useState('standard');
  const [userEmailTemplates, setUserEmailTemplates] = useState<any[]>([]);
  const [allEmailTemplates, setAllEmailTemplates] = useState(OA_EMAIL_TEMPLATES);
  const [showTemplateManager, setShowTemplateManager] = useState(false);
  const [oaReportingInputMode, setOaReportingInputMode] = useState<'onboarded' | 'upload'>('onboarded');
  const [oaReportingFile, setOaReportingFile] = useState<File | null>(null);
  const [oaUploadActionType, setOaUploadActionType] = useState('');

  // Task history and tabs
  const [activeTab, setActiveTab] = useState<'result' | 'history'>('result');
  const [taskHistory, setTaskHistory] = useState<any[]>([]);
  const [historyLoading, setHistoryLoading] = useState(true);

  // File upload for LaTeX conversion and OCR
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [outputFilename, setOutputFilename] = useState('');
  const [outputFormat, setOutputFormat] = useState<'txt' | 'json' | 'docx'>('txt');
  const [selectedHistoryItem, setSelectedHistoryItem] = useState<any>(null);
  const [isDragOver, setIsDragOver] = useState(false);

  // Translation task input mode
  const [translationInputMode, setTranslationInputMode] = useState<'onboarded' | 'upload' | 'text' | null>(null);

  // Antecedent basis check input mode
  const [antecedentInputMode, setAntecedentInputMode] = useState<'onboarded' | 'raw_text' | 'upload' | null>(null);

  // Custom analysis input mode
  const [customAnalysisInputMode, setCustomAnalysisInputMode] = useState<'onboarded' | 'raw_text' | 'upload' | null>(null);
  const [customAnalysisTextContent, setCustomAnalysisTextContent] = useState('');  // For raw text content separate from instructions

  // Technical outline input mode
  const [technicalOutlineInputMode, setTechnicalOutlineInputMode] = useState<'raw_text' | 'upload' | null>(null);

  // Parts process outline input mode
  const [partsProcessOutlineInputMode, setPartsProcessOutlineInputMode] = useState<'raw_text' | 'upload' | null>(null);

  // Direct patent number entry mode for document-analysis tasks
  const [docInputMode, setDocInputMode] = useState<'dropdown' | 'direct'>('dropdown');
  const [choosingExistingDocument, setChoosingExistingDocument] = useState(false);
  const autoSelectionAttempted = useRef('');
  const [directPatentNumber, setDirectPatentNumber] = useState('');
  const [detectedDocType, setDetectedDocType] = useState<string | null>(null);
  const [directInputError, setDirectInputError] = useState('');

  // IR tasks input mode
  const [irInputMode, setIrInputMode] = useState<'onboarded' | 'raw_text' | 'upload' | null>(null);
  const [irTextContent, setIrTextContent] = useState('');  // For raw text content

  // Prior Art Analysis task
  const [priorArtTechDescription, setPriorArtTechDescription] = useState('');
  const [priorArtResourceId, setPriorArtResourceId] = useState('');
  const [priorArtPubNumbers, setPriorArtPubNumbers] = useState(''); // newline/comma-separated

  // Patent Drawing task
  const [patentDrawingFile, setPatentDrawingFile] = useState<File | null>(null);
  const [patentDrawingInstruction, setPatentDrawingInstruction] = useState('');

  // Draft tasks input mode
  const [draftInputMode, setDraftInputMode] = useState<'onboarded' | 'raw_text' | 'upload' | null>(null);
  const [draftTextContent, setDraftTextContent] = useState('');  // For raw text content
  const [draftInstructions, setDraftInstructions] = useState('');  // For additional instructions
  const [draftBackgroundOutputFormat, setDraftBackgroundOutputFormat] = useState<'sections' | 'paragraphs'>('sections');
  const [draftOutputFormat, setDraftOutputFormat] = useState<'sections' | 'paragraphs'>('sections');

  // Document search for translation task
  const [documentSearchQuery, setDocumentSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SessionDocument[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  // Fetch documents from API
  const [sessionDocuments, setSessionDocuments] = useState<SessionDocument[]>([]);
  const [documentsLoading, setDocumentsLoading] = useState(true);

  // Notification store
  const { addNotification } = useNotificationStore();
  const { showToast, copyResult } = useTaskResultCopy();
  const addTaskAttachment = useSessionStore(state => state.addTaskAttachment);

  // Account info for premium checking
  const { user } = useAuth();
  const { isPaid } = usePremium();
  const [accountInfo, setAccountInfo] = useState<any>(null);

  // Elapsed time timer for processing
  useEffect(() => {
    if (isProcessing) {
      processingStartTime.current = Date.now();
      setElapsedSeconds(0);
      const interval = setInterval(() => {
        if (processingStartTime.current) {
          setElapsedSeconds(Math.floor((Date.now() - processingStartTime.current) / 1000));
        }
      }, 1000);
      return () => clearInterval(interval);
    } else {
      processingStartTime.current = null;
    }
  }, [isProcessing]);

  // Fetch documents when panel opens
  useEffect(() => {
    const loadDocuments = async () => {
      setDocumentsLoading(true);
      try {
        const documents = await documentApi.fetchSessionDocuments(100);
        setSessionDocuments(documents);
      } catch (error) {
        console.error('Failed to load documents:', error);
      } finally {
        setDocumentsLoading(false);
      }
    };

    loadDocuments();
  }, []);

  // Fetch account info when panel opens
  useEffect(() => {
    const fetchAccountInfo = async () => {
      if (!user) return;
      try {
        const response = await userApi.fetchAccountInfo();
        if (response && !response.error) {
          setAccountInfo(response);
        }
      } catch (error) {
        console.error('Failed to fetch account info:', error);
      }
    };
    fetchAccountInfo();
  }, [user]);

  // Clear result and form when task changes
  useEffect(() => {
    setResult(null);
    setChoosingExistingDocument(false);
    autoSelectionAttempted.current = '';
    setError('');
    setTextInput('');
    setSelectedResourceId('');
    setSelectedResourceType('');
    setUploadedFile(null);
    setActiveTab('result');
    setTranslationInputMode(null);
    setAntecedentInputMode(null);
    setTechnicalOutlineInputMode(null);
    setPartsProcessOutlineInputMode(null);
    setDraftInputMode(null);
    setDraftTextContent('');
    setDraftInstructions('');
    setDraftBackgroundOutputFormat('sections');
    setDocInputMode('dropdown');
    setDirectPatentNumber('');
    setDetectedDocType(null);
    setDirectInputError('');
  }, [task.id]);

  // Debounced document search
  useEffect(() => {
    const searchDocuments = async () => {
      if (!documentSearchQuery.trim()) {
        setSearchResults([]);
        setIsSearching(false);
        return;
      }

      if (documentSearchQuery.trim().length < 2) {
        setSearchResults([]);
        setIsSearching(false);
        return;
      }

      console.log('[Document Search] Searching for:', documentSearchQuery);
      setIsSearching(true);
      try {
        const results = await documentApi.searchDocuments(documentSearchQuery, 50);
        console.log('[Document Search] Results:', results);
        setSearchResults(results);
      } catch (error) {
        console.error('[Document Search] Failed:', error);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    };

    const timeoutId = setTimeout(searchDocuments, 1000);
    return () => clearTimeout(timeoutId);
  }, [documentSearchQuery]);


  // Function to load user email templates
  const loadUserTemplates = async () => {
    try {
      const response = await userApi.fetchEmailTemplates();
      const userTemplates = response.templates || [];
      setUserEmailTemplates(userTemplates);

      // Merge default and user templates
      const userTemplatesFormatted = userTemplates.map((t: any) => ({
        id: t.prompt_id,
        name: t.name,
        description: t.description || 'Custom template',
        structure: t.template_structure,
      }));

      setAllEmailTemplates([...OA_EMAIL_TEMPLATES, ...userTemplatesFormatted]);
    } catch (error) {
      console.error('Failed to load user email templates:', error);
      // On error, just use default templates
      setAllEmailTemplates(OA_EMAIL_TEMPLATES);
    }
  };

  // Load user email templates on mount
  useEffect(() => {
    loadUserTemplates();
  }, []);

  // Reusable function to load task history
  const loadTaskHistory = async () => {
    setHistoryLoading(true);
    try {
      // Use task.id as the filter to get specific task history
      const response = await taskApi.getTaskHistory(task.id);

      const allHistory = (response as any).history || [];

      // Filter by task ID specifically
      const filteredHistory = allHistory.filter((item: any) => {
        return item.task_type === task.id;
      });

      // Sort by newest first
      const sortedHistory = filteredHistory.sort((a: any, b: any) => {
        const dateA = new Date(a.time_received || a.timestamp).getTime();
        const dateB = new Date(b.time_received || b.timestamp).getTime();
        return dateB - dateA;
      });

      setTaskHistory(sortedHistory);
    } catch (error) {
      console.error('Failed to load task history:', error);
      setTaskHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  // Fetch task history when panel opens or task changes
  useEffect(() => {
    // Clear selected history item when task changes
    setSelectedHistoryItem(null);
    // Reset translation input mode when task changes
    setTranslationInputMode(null);
    // Reset custom analysis input mode when task changes
    setCustomAnalysisInputMode(null);
    setCustomAnalysisTextContent('');
    // Reset IR input mode when task changes
    setIrInputMode(null);
    setIrTextContent('');
    loadTaskHistory();
  }, [task.id]);

  // Fetch session applications for OA tasks
  useEffect(() => {
    const isOATask = task.id.includes('oa_');
    if (!isOATask) return;

    const loadApplications = async () => {
      setApplicationsLoading(true);
      try {
        const apps = await patentApi.getSessionApplications();

        // Filter out FALLBACK- applications
        const filteredApps = apps.filter(app => !app.application_id.startsWith('FALLBACK-'));

        // Get active applications from session context
        const sessionContextStr = localStorage.getItem('session_context');
        let activeAppIds: string[] = [];
        if (sessionContextStr && sessionContextStr !== 'undefined') {
          try {
            const sessionContext = JSON.parse(sessionContextStr);
            activeAppIds = sessionContext.current_applications || [];
          } catch (e) {
            console.error('Error parsing session context:', e);
          }
        }

        // Sort applications: active ones first, then by filing date
        const sortedApps = filteredApps.sort((a, b) => {
          const aActive = activeAppIds.includes(a.application_id);
          const bActive = activeAppIds.includes(b.application_id);

          if (aActive !== bActive) {
            return aActive ? -1 : 1;
          }

          // Then sort by filing date (newest first)
          const aDate = a.filing_date ? new Date(a.filing_date).getTime() : 0;
          const bDate = b.filing_date ? new Date(b.filing_date).getTime() : 0;
          return bDate - aDate;
        });

        setSessionApplications(sortedApps.map(app => ({
          ...app,
          isActive: activeAppIds.includes(app.application_id)
        })));
      } catch (error) {
        console.error('Failed to load applications:', error);
      } finally {
        setApplicationsLoading(false);
      }
    };

    loadApplications();
  }, [task.id]);

  // Fetch prosecution documents when application is selected for OA tasks
  useEffect(() => {
    const isOATask = task.id.includes('oa_');
    if (!isOATask || !selectedApplication) return;

    const loadProsecutionDocs = async () => {
      setProsecutionDocsLoading(true);
      try {
        const result = await patentApi.fetchProsecutionDocInfo(selectedApplication);
        console.log('Prosecution doc info result:', result);

        // Handle different response formats
        let prosInfoArray: any[] = [];
        if (result.pros_info && Array.isArray(result.pros_info)) {
          prosInfoArray = result.pros_info;
        } else if (Array.isArray(result)) {
          prosInfoArray = result;
        }

        // Map prosecution documents
        const docs = prosInfoArray
          .filter(doc => Array.isArray(doc.downloadOptionBag))
          .map(doc => {
            const pdf = doc.downloadOptionBag.find((d: any) => d.mimeTypeIdentifier === 'PDF');
            return {
              date: new Date(doc.officialDate).toLocaleDateString(),
              rawDate: new Date(doc.officialDate),
              pdfUrl: pdf?.downloadUrl || null,
              description: doc.documentCodeDescriptionText || doc.documentCode,
              documentCode: doc.documentCode,
              docId: doc.documentIdentifier
            };
          })
          .filter(doc => !!doc.pdfUrl)
          .sort((a, b) => b.rawDate.getTime() - a.rawDate.getTime());

        // Filter to only office action documents (including restrictions)
        const oaDocs = docs.filter(doc => {
          const desc = doc.description.toLowerCase();
          const code = doc.documentCode;
          // Include office actions, rejections, allowances, notifications, and restriction requirements
          return /(office action|rejection|allowance|notification)/i.test(desc) || code === 'CTRS';
        });

        setProsecutionDocs(oaDocs);
      } catch (error) {
        console.error('Failed to load prosecution documents:', error);
        setProsecutionDocs([]);
      } finally {
        setProsecutionDocsLoading(false);
      }
    };

    loadProsecutionDocs();
  }, [task.id, selectedApplication]);

  // Helper function to format timestamps correctly in local time
  const formatTimestamp = (timestamp: string | Date | undefined): string => {
    if (!timestamp) return 'Unknown time';

    try {
      let dateStr = timestamp;

      // If timestamp is a string, check if it has a timezone indicator
      if (typeof dateStr === 'string') {
        // Check if timestamp has timezone indicator (Z, +HH:MM, or -HH:MM)
        const hasTimezone = /[Z]$|[+-]\d{2}:\d{2}$/.test(dateStr);

        // If no timezone indicator, assume UTC and add 'Z'
        if (!hasTimezone && dateStr.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/)) {
          // Remove any decimal seconds first
          const baseTimestamp = dateStr.split('.')[0];
          dateStr = baseTimestamp + 'Z';
        }
        // If timestamp has microseconds, truncate to milliseconds
        else if (dateStr.includes('.')) {
          const parts = dateStr.split('.');
          if (parts.length === 2) {
            const fractionalPart = parts[1];
            // Check if there's more than 3 digits before timezone indicator
            const timezoneMatch = fractionalPart.match(/([+-]\d{2}:\d{2}|Z)$/);
            if (timezoneMatch) {
              const timezone = timezoneMatch[0];
              const digits = fractionalPart.substring(0, fractionalPart.indexOf(timezone));
              if (digits.length > 3) {
                // Truncate to 3 digits (milliseconds)
                dateStr = parts[0] + '.' + digits.substring(0, 3) + timezone;
              }
            }
          }
        }
      }

      const date = new Date(dateStr);
      return date.toLocaleString();
    } catch (error) {
      console.error('Error formatting timestamp:', timestamp, error);
      return String(timestamp);
    }
  };

  // Get category info
  const categoryKey = TASK_TO_CATEGORY[task.id] || 'legacy';
  const categoryInfo = TASK_CATEGORY_INFO[categoryKey];
  const CategoryIcon = categoryInfo?.icon;

  // Extract result data from result object, checking multiple possible properties
  const getResultData = (resultObj: any) => {
    if (!resultObj) return null;

    // Check multiple possible property names where the actual data might be stored
    // Priority: result > output > analysis > use the object itself
    if (resultObj.result !== undefined) return resultObj.result;
    if (resultObj.output !== undefined) return resultObj.output;
    if (resultObj.analysis !== undefined) return resultObj.analysis;

    // If none of the above exist, assume the object itself is the data
    return resultObj;
  };

  // Parse result output - it comes as a string that may need parsing
  const getParsedResult = (output: any) => {
    console.log('[getParsedResult] Input type:', typeof output);
    console.log('[getParsedResult] Input (first 500 chars):', typeof output === 'string' ? output.substring(0, 500) : output);

    if (!output) return null;

    // If it's already an object, return it
    if (typeof output === 'object') {
      console.log('[getParsedResult] Already an object, returning as-is');
      return output;
    }

    // If it's a string, try to parse it as JSON
    if (typeof output === 'string') {
      const trimmed = output.trim();

      // Only try to parse if it looks like JSON/dict (starts with { or [)
      if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) {
        console.log('[getParsedResult] Does not look like JSON/dict, returning as-is');
        return output;
      }

      try {
        // Try to parse as JSON first
        const parsed = JSON.parse(output);
        console.log('[getParsedResult] Successfully parsed as JSON');
        return parsed;
      } catch (jsonError) {
        console.log('[getParsedResult] JSON parse failed, trying Python dict conversion');
        // If parsing fails, try to convert Python dict to JSON
        let jsonStr = output;
        try {

          // Handle Python boolean and null values first
          jsonStr = jsonStr.replace(/\bTrue\b/g, 'true');
          jsonStr = jsonStr.replace(/\bFalse\b/g, 'false');
          jsonStr = jsonStr.replace(/\bNone\b/g, 'null');

          // Step 1: Replace single quotes with double quotes for keys (pattern: 'key':)
          jsonStr = jsonStr.replace(/'([^']+?)'(\s*):/g, '"$1"$2:');

          // Step 2: Protect double-quoted strings by replacing them with placeholders
          const doubleQuotedStrings: string[] = [];
          jsonStr = jsonStr.replace(/"([^"]*)"/g, (match, content) => {
            const index = doubleQuotedStrings.length;
            doubleQuotedStrings.push(match);
            return `__PLACEHOLDER_${index}__`;
          });

          // Step 3: Now convert single-quoted values and arrays (double-quoted strings are protected)
          // Convert simple string values: 'value' -> "value"
          jsonStr = jsonStr.replace(/:\s*'([^']*)'/g, ': "$1"');

          // Convert array items: ['item', 'item'] -> ["item", "item"]
          jsonStr = jsonStr.replace(/\[(\s*)'([^']*?)'/g, '[$1"$2"');
          jsonStr = jsonStr.replace(/,(\s*)'([^']*?)'/g, ',$1"$2"');
          jsonStr = jsonStr.replace(/'(\s*)\]/g, '"$1]');

          // Step 4: Restore double-quoted strings
          jsonStr = jsonStr.replace(/__PLACEHOLDER_(\d+)__/g, (match, index) => {
            return doubleQuotedStrings[parseInt(index)];
          });

          console.log('[getParsedResult] Converted string (first 3000 chars):', jsonStr.substring(0, 3000));
          console.log('[getParsedResult] Converted string (chars 2600-2700):', jsonStr.substring(2600, 2700));

          // Now parse the converted string
          const parsed = JSON.parse(jsonStr);
          console.log('[getParsedResult] Successfully parsed Python dict');
          return parsed;
        } catch (e) {
          console.error('[getParsedResult] Failed to parse Python dict. Error:', e);
          console.error('[getParsedResult] Input (first 500 chars):', output.substring(0, 500));
          if (jsonStr && typeof jsonStr === 'string') {
            console.error('[getParsedResult] Converted string around error position (chars 2600-2700):', jsonStr.substring(2600, 2700));
          }
          // If all parsing fails, return the string as-is
          return output;
        }
      }
    }

    return output;
  };

  const getDocumentIdFromResult = (taskResult: any, fallbackId?: string): string | undefined => {
    if (!taskResult) return fallbackId;

    const candidateFields = [
      'document_id',
      'doc_id',
      'docId',
      'documentId',
      'doc_no',
      'docNo',
      'selected_number',
      'selectedNumber',
      'selected_doc_id',
      'selectedDocId',
      'selected_document',
      'selectedDocument',
      'document',
      'doc',
      'application_number',
      'applicationNumber',
      'resource_id',
      'resourceId',
    ];

    const extractFrom = (source: any): string | undefined => {
      if (!source) return undefined;
      for (const field of candidateFields) {
        const value = source[field];
        if (typeof value === 'string' && value.trim().length > 0) {
          return value.trim();
        }
        if (typeof value === 'number') {
          return value.toString();
        }
      }
      return undefined;
    };

    return extractFrom(taskResult) ||
           extractFrom(taskResult?.metadata) ||
           (fallbackId ? fallbackId.trim() : undefined);
  };

  const normalizeResourceId = (resourceId?: string | null) => {
    if (!resourceId) return undefined;
    const parts = resourceId.split('-');
    const withoutType = parts.length > 1 ? parts.slice(1).join('-') : resourceId;
    return withoutType || resourceId;
  };

  // Helper to get active document IDs with their types from localStorage
  const getActiveDocuments = (): Map<string, string> => {
    try {
      const sessionContextStr = localStorage.getItem('session_context');
      if (!sessionContextStr || sessionContextStr === 'undefined') return new Map();

      const sessionContext = JSON.parse(sessionContextStr);
      const activeDocuments = new Map<string, string>();

      // Map session_context keys to document types
      const typeMapping: Record<string, string> = {
        'current_patents': 'patent',
        'current_publications': 'publication',
        'current_applications': 'application',
        'pros_docs': 'application', // prosecution docs are usually applications
        'resource_ids': 'upload'
      };

      // Collect all active document IDs with their types
      Object.entries(typeMapping).forEach(([key, docType]) => {
        if (sessionContext[key] && Array.isArray(sessionContext[key])) {
          sessionContext[key].forEach((id: string) => {
            activeDocuments.set(id, docType);
          });
        }
      });

      return activeDocuments;
    } catch (error) {
      console.error('Error parsing session_context:', error);
      return new Map();
    }
  };

  // Determine if task is office action related
  const isOfficeActionTask = () => {
    return task.id.includes('oa_') || task.id.includes('form_');
  };

  // Filter and sort documents based on task type
  const availableDocuments = useMemo(() => {
    const activeDocs = getActiveDocuments();
    let allDocs: SessionDocument[] = [];

    if (isOfficeActionTask()) {
      // Office action tasks only show applications
      allDocs = sessionDocuments.filter(doc => doc.file_type === 'application');
    } else {
      // Spec-related tasks show patents, publications, uploads, and foreign
      const allowedTypes = ['patent', 'publication', 'upload', 'foreign'];
      allDocs = sessionDocuments.filter(doc => allowedTypes.includes(doc.file_type));
    }

    // Add isActive flag and sort: active documents first, then by name
    const docsWithActive = allDocs.map(doc => {
      // Extract the ID part (e.g., 'publication-20190190617' -> '20190190617')
      const idParts = doc.resource_id.split('-');
      const docId = idParts.length > 1 ? idParts.slice(1).join('-') : doc.resource_id;

      // Check if this document is active by matching both ID and type
      const isActive = activeDocs.has(docId) && activeDocs.get(docId) === doc.file_type;
      return {
        ...doc,
        isActive
      };
    });

    return docsWithActive.sort((a, b) => {
      if (a.isActive !== b.isActive) {
        return a.isActive ? -1 : 1;
      }
      const nameA = a.file_name || a.resource_id || '';
      const nameB = b.file_name || b.resource_id || '';
      return nameA.localeCompare(nameB);
    });
  }, [sessionDocuments, task.id]);

  // Auto-select first document when availableDocuments changes
  // Skip for the 5 legacy doc tasks that now show a method chooser first
  const legacyDocTaskIds = ['parts_list', 'spec_map', 'claims_support', 'claim_chart', 'claims_with_numerals'];
  useEffect(() => {
    // Only auto-select for tasks that require document selection
    // Skip for legacy doc tasks (they show a method chooser) and direct entry mode
    if (autoSelectionAttempted.current !== task.id && docInputMode === 'dropdown' && requiresDocumentSelection() && !task.id.includes('oa_') && !selectedResourceId && availableDocuments.length > 0) {
      autoSelectionAttempted.current = task.id;
      const document = selectTaskDocument(availableDocuments, preferredDocument);
      if (document) {
        setSelectedResourceId(document.resource_id);
        setSelectedResourceType(document.file_type);
        setChoosingExistingDocument(true);
      }
    }
  }, [availableDocuments, task.id, selectedResourceId, preferredDocument, docInputMode]);

  // Determine input requirements based on task type
  const requiresDocumentSelection = () => {
    // Exclude tasks that have their own custom input UI
    if (task.id === 'translate_document' || task.id === 'antecedent_basis_check' || task.id === 'technical_outline' || task.id === 'parts_process_outline' || task.id === 'prior_art_analysis' || task.id === 'patent_drawing' || task.id.includes('draft_')) return false;
    // Check if task has resource_interface that requires resource_id
    return task.resource_interface?.input?.resource_id === true;
  };

  const hasClaimsSupportOptions = () => {
    // Check if task supports description/claims/support checkboxes
    return task.resource_interface?.input?.use_entire_description === true ||
           task.resource_interface?.input?.use_entire_claims === true ||
           task.resource_interface?.input?.include_support === true;
  };

  const requiresTextInput = () => {
    // Custom analysis needs both document selection AND text input
    if (task.id === 'custom_analysis') {
      return true;
    }
    // Draft tasks have their own custom input UI, don't use general text input
    if (task.id.includes('draft_')) {
      return false;
    }
    // If document selection is required, text input is optional
    // Otherwise, text input is required
    return !requiresDocumentSelection();
  };

  const requiresPatentSelection = () => {
    // Tasks that optionally support patent selection (plain text input)
    return task.id.includes('form_');
  };

  const requiresApplicationNumber = () => {
    return task.id.includes('form_');
  };

  const isTranslationTask = () => {
    return task.id.startsWith('translate_');
  };

  const requiresOfficeActionDocument = () => {
    // OA Timeline only needs application, not a specific OA document
    // OA Reporting Email in upload mode doesn't need a document selection
    if (task.id === 'oa_reporting_email' && oaReportingInputMode === 'upload') return false;
    return task.id.includes('oa_') && task.id !== 'oa_timeline';
  };

  // Handle adding task result to chat
  const handleAddToChat = (taskResult: any, taskName: string, taskType: string, timestamp: string) => {
    // Extract and format the content
    const resultData = getParsedResult(getResultData(taskResult));
    const contentString = typeof resultData === 'string'
      ? resultData
      : JSON.stringify(resultData, null, 2);

    const fallbackDocId = taskResult === result ? normalizeResourceId(selectedResourceId) : undefined;
    const documentId = getDocumentIdFromResult(taskResult, fallbackDocId);

    // Create task attachment
    const attachment: TaskResultAttachment = {
      id: `task-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      taskName: taskName,
      taskType: taskType,
      timestamp: timestamp,
      content: contentString,
      documentId,
    };

    // Add to global store
    addTaskAttachment(attachment);

    // Show notification
    addNotification({
      type: 'success',
      title: 'Added to Chat',
      message: `${taskName} result has been added to your chat input.`,
      duration: 3000,
    });
  };

  const getInputPlaceholder = () => {
    if (task.id.includes('ir_')) {
      return 'Paste your invention record text here...';
    } else if (task.id.includes('draft_')) {
      return 'Enter your invention disclosure or technical content here...';
    } else if (task.id.includes('oa_')) {
      return 'Paste the office action text here...';
    } else if (task.id.includes('form_')) {
      return 'Enter the required information here...';
    } else if (isTranslationTask()) {
      return 'Enter the text you want to translate...';
    } else if (task.id === 'parts_list') {
      return 'The parts list will be generated from the selected patent document...';
    } else if (task.id === 'spec_map') {
      return 'The specification map will be generated from the selected patent document...';
    } else if (task.id === 'claims_support') {
      return 'Claims support analysis will be performed on the selected patent document...';
    } else if (task.id === 'claims_with_numerals') {
      return 'Reference numerals will be added to claims from the selected patent document...';
    } else if (task.id === 'custom_analysis') {
      return 'Describe what analysis or information you need (e.g., "Compare the claims to identify overlapping scope" or "Summarize the key technical advantages")';
    }
    return 'Enter your text content here...';
  };

  const getTaskInstructions = () => {
    // Document Analysis Tasks
    if (task.id === 'parts_list') {
      return 'Automatically extract and organize all components, parts, and elements from the patent specification. The generated list includes reference numerals and detailed descriptions, making it easier to track components across your document.';
    } else if (task.id === 'spec_map') {
      return 'Create a comprehensive map linking specification details to parts, features, and reference numerals. This helps ensure consistency between your drawings, detailed description, and claims.';
    } else if (task.id === 'antecedent_basis_check') {
      return 'Analyze each claim to verify that all elements have proper antecedent basis. The tool checks that claim terms are properly introduced before being referenced, identifies missing antecedents, and ensures clear referential relationships throughout your claim set.';
    } else if (task.id === 'technical_outline') {
      return 'Generate a comprehensive technical outline from your invention disclosure for patent specification drafting. The outline covers technical field, background, summary, detailed description (components, processes, features), alternative embodiments, applications, implementation details, and claims considerations. Provide a detailed disclosure for best results.';
    } else if (task.id === 'parts_process_outline') {
      return 'Generate a detailed parts hierarchy and process outline from your invention disclosure. This task focuses on granular technical details, identifying specific parts with their technical language, hierarchical relationships (parts to subparts), functional relationships between components, process steps with dependencies, and system flows. Ideal for detailed patent specification drafting where precise component relationships and process flows need to be mapped.';
    } else if (task.id === 'claims_support') {
      return 'Verify that each claim element is properly supported by the specification. The analysis identifies which portions of the specification support each claim limitation, helping you strengthen your claims and prepare for prosecution.';
    } else if (task.id === 'claims_with_numerals') {
      return 'Automatically insert reference numerals from your specification into claim language. This enhances claim clarity by linking claim elements to specific components shown in the drawings and described in the detailed description.';
    } else if (task.id === 'custom_analysis') {
      return 'Run specialized analysis on your patent documents using custom prompts. Define your own analysis criteria to extract specific information, check for particular issues, or generate custom reports tailored to your needs.';
    } else if (task.id === 'technical_outline') {
      return 'Generate a comprehensive technical outline from your invention disclosure to guide patent specification drafting. The outline organizes your disclosure into structured subsections with specific content points, word targets, and technical depth levels, providing a roadmap for creating a complete and well-organized patent specification.';

    // Invention Record Tasks
    } else if (task.id.includes('ir_metadata')) {
      return 'Extract metadata including inventors, assignees, filing dates, and technical classifications from your invention record. This structured data can be used to populate forms and track invention details.';
    } else if (task.id.includes('ir_innovation')) {
      return 'Analyze the innovation aspects and technological advancements described in your invention record. Identify key innovations, technical improvements, and competitive advantages that should be highlighted in the patent application.';
    } else if (task.id.includes('ir_tech_class')) {
      return 'Classify your invention into appropriate technology categories and patent classifications (CPC/IPC). Proper classification helps with prior art searching and ensures your application is examined by specialists in the relevant field.';

    // Patent Drafting Tasks
    } else if (task.id === 'draft_detailed_description') {
      return 'Generate a comprehensive detailed description section for your patent application. The system analyzes your input and creates a thorough technical description with proper structure, terminology, and reference numerals.';
    } else if (task.id === 'draft_other_embodiments') {
      return 'Generate alternative embodiments and variations to broaden patent scope. The tool suggests different implementations, configurations, and use cases that can strengthen your patent protection.';
    } else if (task.id === 'draft_claims') {
      return 'Generate patent claims with proper formatting and structure from your disclosure. The system creates independent and dependent claims following USPTO requirements, with appropriate antecedent basis and claim differentiation.';
    } else if (task.id === 'draft_background') {
      return 'Generate a background section describing the field and existing technology. The analysis identifies the technical problem, discusses limitations of prior solutions, and sets up the context for your invention.';
    } else if (task.id === 'draft_summary') {
      return 'Generate a concise summary section for your patent application. The summary highlights key aspects of the invention and provides a high-level overview suitable for patent abstracts and summaries.';

    // Office Action Tasks
    } else if (task.id.includes('oa_extract')) {
      return 'Extract key information and requirements from the office action document. The analysis identifies rejections, objections, deadlines, cited prior art, and specific examiner requirements to help you plan your response strategy.';
    } else if (task.id.includes('oa_analyze')) {
      return 'Analyze office action issues and provide detailed assessment of rejections. The tool examines each rejection, evaluates its strength, and suggests potential response strategies based on the cited prior art and examiner\'s reasoning.';
    } else if (task.id.includes('oa_summary_art')) {
      return 'Summarize the prior art references cited in the office action. Get clear, concise summaries of each cited document including their key teachings, relevant figures, and how the examiner applied them against your claims.';
    } else if (task.id.includes('oa_reporting_email')) {
      return 'Generate a professional email report summarizing the office action for client communication. Choose from various templates to create clear, client-friendly explanations of office action issues, deadlines, and recommended next steps.';
    } else if (task.id.includes('oa_timeline')) {
      return 'Create a timeline of prosecution history from the application documents. Track all office actions, responses, amendments, and key events to understand the prosecution history and identify patterns or issues.';

    // Translation & Document Processing
    } else if (isTranslationTask()) {
      return 'Translate patent documents while preserving technical accuracy and legal terminology. The translation maintains proper claim structure, reference numerals, and technical terms to ensure the translated document meets patent office requirements.';
    } else if (task.id === 'latex_to_docx') {
      return 'Convert LaTeX documents (including ZIP archives with multiple files) to Microsoft Word format. Mathematical equations, special characters, and formatting are preserved during conversion, making it easier to edit and finalize your documents.';
    } else if (task.id === 'ocr_pdf') {
      return 'Extract text from scanned PDF documents using advanced OCR technology. Choose between text, Word, or JSON output formats. JSON output includes positional data for advanced processing and analysis.';
    }

    return task.description;
  };

  // Get display name for a document (shows filename for uploads, ID for others)
  const getDocumentDisplayName = (documentId: string, resourceType: string) => {
    if (resourceType === 'upload') {
      // For uploads, look up the filename from availableDocuments
      const doc = availableDocuments.find(d => d.resource_id === documentId || d.resource_id === `upload-${documentId}`);
      return doc?.file_name || documentId;
    }
    // For patents/publications/applications, show the ID as-is
    return documentId;
  };

  // Navigate to the Documents page with the document open and task result shown
  const handleViewSpecification = (taskResult: any) => {
    const docNumber = taskResult.selected_number || taskResult.document_id || taskResult.doc_id;
    const resourceType = taskResult.resource_type || selectedResourceType || 'patent';
    if (!docNumber) return;

    // Build a document object compatible with Main.tsx's handleDocumentSelect / setSelectedDocument
    const docObj: any = {
      resource_id: `${resourceType}-${docNumber}`,
      file_name: `${resourceType === 'publication' ? 'Publication' : resourceType === 'foreign_patent' ? 'Foreign Patent' : 'Patent'} ${docNumber}`,
      file_type: resourceType,
    };

    // Build a TaskResultAttachment for the DocumentViewer
    const parsedData = getParsedResult(getResultData(taskResult));
    const contentStr = typeof parsedData === 'string' ? parsedData : JSON.stringify(parsedData);
    const taskResultAttachment = {
      id: taskResult.analysis_id || `task-${Date.now()}`,
      taskName: task.name,
      taskType: task.id,
      timestamp: taskResult.timestamp || taskResult.time_received || new Date().toISOString(),
      content: contentStr,
      documentId: docNumber,
    };

    window.dispatchEvent(new CustomEvent('view-document-with-task', {
      detail: { document: docObj, taskResult: taskResultAttachment }
    }));
  };

  // Check if a result is associated with a viewable patent/publication/foreign document
  const isViewableDocResult = (taskResult: any): boolean => {
    if (!taskResult) return false;
    const resourceType = taskResult.resource_type || selectedResourceType || '';
    const docNumber = taskResult.selected_number || taskResult.document_id || taskResult.doc_id;
    if (!docNumber) return false;
    // Only show for patent/publication/foreign types (not uploads, applications, or office actions)
    return ['patent', 'publication', 'foreign_patent', 'foreign'].includes(resourceType);
  };

  const getTaskInputError = (): string | null => {
    if (task.id.includes('oa_')) {
      if (task.id === 'oa_reporting_email' && oaReportingInputMode === 'upload') {
        if (!oaReportingFile) {
          return 'Please upload an office action PDF';
        }
        if (!oaUploadActionType) {
          return 'Please select the action type';
        }
      } else {
        if (!selectedApplication) {
          return 'Please select an application';
        }
        if (requiresOfficeActionDocument() && !selectedOfficeAction) {
          return 'Please select an office action document';
        }
      }
    }
    else if (task.id === 'custom_analysis') {
      if (!customAnalysisInputMode) {
        return 'Please select an input method';
      }
      if (customAnalysisInputMode === 'onboarded' && !selectedResourceId) {
        return 'Please select a document to analyze';
      }
      if (customAnalysisInputMode === 'raw_text' && !customAnalysisTextContent.trim()) {
        return 'Please enter text content to analyze';
      }
      if (customAnalysisInputMode === 'upload' && !uploadedFile) {
        return 'Please upload a document to analyze';
      }
      if (!textInput.trim()) {
        return 'Please enter analysis instructions';
      }
    }
    else if (task.id === 'prior_art_analysis') {
      if (!priorArtTechDescription.trim() && !priorArtResourceId.trim()) {
        return 'Please enter a technology description or patent number';
      }
      if (!priorArtPubNumbers.trim()) {
        return 'Please enter at least one prior art publication number';
      }
    }
    else if (task.id === 'patent_drawing') {
      if (!patentDrawingFile && !patentDrawingInstruction.trim()) {
        return 'Please upload an image, describe the drawing, or both';
      }
    }
    else if (task.id.includes('ir_')) {
      if (!irInputMode) {
        return 'Please select an input method';
      }
      if (irInputMode === 'onboarded' && !selectedResourceId) {
        return 'Please select a document';
      }
      if (irInputMode === 'raw_text' && !irTextContent.trim()) {
        return 'Please enter invention record text';
      }
      if (irInputMode === 'upload' && !uploadedFile) {
        return 'Please upload a document';
      }
    }
    else if (task.id.includes('draft_')) {
      if (!draftInputMode) {
        return 'Please select an input method';
      }
      if (draftInputMode === 'onboarded' && !selectedResourceId) {
        return 'Please select a reference document';
      }
      if (draftInputMode === 'raw_text' && !draftTextContent.trim()) {
        return 'Please enter disclosure text';
      }
      if (draftInputMode === 'upload' && !uploadedFile) {
        return 'Please upload a disclosure document';
      }
    }
    else if (requiresDocumentSelection()) {
      if (docInputMode === 'direct') {
        if (!directPatentNumber.trim()) {
          return 'Please enter a patent or publication number';
        }
        const classified = classifyPatentNumber(directPatentNumber);
        if (!classified) {
          return 'Could not recognize format. Try: 9424461, US20240123456A1, 17/234,567, or EP1234567A1';
        }
      } else {
        if (!selectedResourceId) {
          return 'Please choose an input method and select a document';
        }
      }
    }
    else if (task.id !== 'latex_to_docx' && task.id !== 'ocr_pdf' && task.id !== 'antecedent_basis_check' && task.id !== 'technical_outline' && task.id !== 'parts_process_outline' && task.id !== 'prior_art_analysis' && task.id !== 'patent_drawing' && !(task.id === 'translate_document' && uploadedFile) && !textInput.trim() && !patentNumber.trim()) {
      return 'Please enter text content or select a patent document';
    }
    if (task.id === 'translate_document') {
      if (!translationInputMode) {
        return 'Please select an input method (onboarded document, upload, or raw text)';
      }
      if (translationInputMode === 'onboarded' && !selectedResourceId) {
        return 'Please select a document to translate';
      }
      if (translationInputMode === 'upload' && !uploadedFile) {
        return 'Please upload a document to translate';
      }
      if (translationInputMode === 'text' && !textInput.trim()) {
        return 'Please enter text to translate';
      }
    }
    return null;
  };

  const handleRunTask = async () => {
    // Check if task is premium-only and user is not premium
    if (task.premium_only && accountInfo?.account_type !== 'premium') {
      addNotification({
        type: 'warning',
        title: 'Premium Feature Required',
        message: `${task.name} is only available for Premium subscribers. Please upgrade to access this feature.`,
        duration: 5000,
      });
      return;
    }

    const validationError = getTaskInputError();
    if (validationError) {
      setError(validationError);
      return;
    }

    setError('');
    setIsProcessing(true);
    setResult(null);

    try {
      let response: any;

      // Determine resourceId, resourceType, and isApplication
      // When in direct entry mode for the 5 legacy tasks, derive from classifyPatentNumber()
      let resourceId = '';
      let effectiveResourceType = selectedResourceType;
      let isApplication = selectedResourceType === 'application';

      const legacyDocTasks = ['parts_list', 'spec_map', 'claims_support', 'claim_chart', 'claims_with_numerals'];
      if (docInputMode === 'direct' && legacyDocTasks.includes(task.id)) {
        const classified = classifyPatentNumber(directPatentNumber);
        if (classified) {
          resourceId = classified.cleanedNumber;
          effectiveResourceType = classified.docType;
          isApplication = classified.isApplication;
        }
      } else if (selectedResourceId) {
        if (selectedResourceType === 'upload') {
          // For uploads, use the entire selectedResourceId
          resourceId = selectedResourceId;
        } else {
          // For patents/publications/applications, extract after the first hyphen
          // e.g., 'publication-20190190617' -> '20190190617'
          resourceId = selectedResourceId.split('-').slice(1).join('-');
        }
      }
      const normalizedDocumentIdForRun = normalizeResourceId(selectedResourceId);

      // Execute task based on task ID
      if (task.id === 'parts_list') {
        response = await taskApi.submitPartsListTask(resourceId, effectiveResourceType, isApplication);
      } else if (task.id === 'spec_map') {
        response = await taskApi.submitSpecMapTask(resourceId, effectiveResourceType, isApplication);
      } else if (task.id === 'claims_support') {
        response = await taskApi.submitClaimsSupportTask(
          resourceId,
          effectiveResourceType,
          isApplication
        );
      } else if (task.id === 'claim_chart') {
        response = await taskApi.submitClaimChartTask(resourceId, effectiveResourceType, isApplication, includeSupport);
      } else if (task.id === 'claims_with_numerals') {
        response = await taskApi.submitClaimsWithNumeralsTask(resourceId, effectiveResourceType, isApplication);
      } else if (task.id === 'ir_metadata') {
        if (irInputMode === 'onboarded') {
          response = await taskApi.submitIRMetadataTask('', 'onboarded', resourceId, selectedResourceType);
        } else if (irInputMode === 'raw_text') {
          response = await taskApi.submitIRMetadataTask(irTextContent, 'raw_text');
        } else if (irInputMode === 'upload') {
          response = await taskApi.submitIRMetadataTaskUpload(uploadedFile!);
        }
      } else if (task.id === 'ir_innovation') {
        if (irInputMode === 'onboarded') {
          response = await taskApi.submitIRInnovationTask('', 'onboarded', resourceId, selectedResourceType);
        } else if (irInputMode === 'raw_text') {
          response = await taskApi.submitIRInnovationTask(irTextContent, 'raw_text');
        } else if (irInputMode === 'upload') {
          response = await taskApi.submitIRInnovationTaskUpload(uploadedFile!);
        }
      } else if (task.id === 'ir_tech_class') {
        if (irInputMode === 'onboarded') {
          response = await taskApi.submitIRTechClassTask('', 'onboarded', resourceId, selectedResourceType);
        } else if (irInputMode === 'raw_text') {
          response = await taskApi.submitIRTechClassTask(irTextContent, 'raw_text');
        } else if (irInputMode === 'upload') {
          response = await taskApi.submitIRTechClassTaskUpload(uploadedFile!);
        }
      } else if (task.id === 'prior_art_analysis') {
        const pubNumbers = priorArtPubNumbers
          .split(/[\n,]+/)
          .map((s) => s.trim())
          .filter(Boolean);
        response = await taskApi.submitPriorArtAnalysisTask({
          technologyDescription: priorArtTechDescription.trim() || undefined,
          resourceId: priorArtResourceId.trim() || undefined,
          priorArtPubNumbers: pubNumbers,
        });
      } else if (task.id === 'patent_drawing') {
        response = await taskApi.submitPatentDrawingTask(
          patentDrawingFile,
          patentDrawingInstruction.trim() || undefined
        );
      } else if (task.id === 'draft_summary') {
        if (draftInputMode === 'onboarded') {
          response = await taskApi.submitDraftSummaryTask('', selectedResourceId, draftInstructions, draftOutputFormat);
        } else if (draftInputMode === 'raw_text') {
          response = await taskApi.submitDraftSummaryTask(draftTextContent, undefined, draftInstructions, draftOutputFormat);
        } else if (draftInputMode === 'upload') {
          response = await taskApi.submitDraftSummaryTaskWithFile(uploadedFile!, draftInstructions, draftOutputFormat);
        }
      } else if (task.id === 'draft_detailed_description') {
        if (draftInputMode === 'onboarded') {
          response = await taskApi.submitDraftDetailedDescriptionTask('', undefined, selectedResourceId, draftInstructions, draftOutputFormat);
        } else if (draftInputMode === 'raw_text') {
          response = await taskApi.submitDraftDetailedDescriptionTask('', draftTextContent, undefined, draftInstructions, draftOutputFormat);
        } else if (draftInputMode === 'upload') {
          response = await taskApi.submitDraftDetailedDescriptionTaskWithFile(uploadedFile!, draftInstructions, draftOutputFormat);
        }
      } else if (task.id === 'draft_background') {
        if (draftInputMode === 'onboarded') {
          response = await taskApi.submitDraftBackgroundTask('', selectedResourceId, draftInstructions, draftBackgroundOutputFormat);
        } else if (draftInputMode === 'raw_text') {
          response = await taskApi.submitDraftBackgroundTask(draftTextContent, undefined, draftInstructions, draftBackgroundOutputFormat);
        } else if (draftInputMode === 'upload') {
          response = await taskApi.submitDraftBackgroundTaskWithFile(uploadedFile!, draftInstructions, draftBackgroundOutputFormat);
        }
      } else if (task.id === 'draft_claims') {
        if (draftInputMode === 'onboarded') {
          response = await taskApi.submitDraftClaimsTask('', selectedResourceId, draftInstructions, draftOutputFormat);
        } else if (draftInputMode === 'raw_text') {
          response = await taskApi.submitDraftClaimsTask(draftTextContent, undefined, draftInstructions, draftOutputFormat);
        } else if (draftInputMode === 'upload') {
          response = await taskApi.submitDraftClaimsTaskWithFile(uploadedFile!, draftInstructions, draftOutputFormat);
        }
      } else if (task.id === 'draft_other_embodiments') {
        if (draftInputMode === 'onboarded') {
          response = await taskApi.submitDraftOtherEmbodimentsTask('', selectedResourceId, draftInstructions, draftOutputFormat);
        } else if (draftInputMode === 'raw_text') {
          response = await taskApi.submitDraftOtherEmbodimentsTask(draftTextContent, undefined, draftInstructions, draftOutputFormat);
        } else if (draftInputMode === 'upload') {
          response = await taskApi.submitDraftOtherEmbodimentsTaskWithFile(uploadedFile!, draftInstructions, draftOutputFormat);
        }
      } else if (task.id === 'oa_extract') {
        response = await taskApi.submitOAExtractTask(selectedOfficeAction, selectedApplication);
      } else if (task.id === 'oa_analyze') {
        response = await taskApi.submitOAAnalyzeTask(selectedOfficeAction, selectedApplication);
      } else if (task.id === 'oa_summary_art') {
        response = await taskApi.submitOASummaryArtTask(selectedOfficeAction, selectedApplication);
      } else if (task.id === 'oa_reporting_email') {
        if (oaReportingInputMode === 'upload' && oaReportingFile) {
          response = await taskApi.submitOAReportingEmailTaskWithFile(oaReportingFile, selectedEmailTemplate, oaUploadActionType);
        } else {
          response = await taskApi.submitOAReportingEmailTask(selectedOfficeAction, selectedApplication, selectedEmailTemplate);
        }
      } else if (task.id === 'oa_timeline') {
        response = await taskApi.submitOATimelineTask(selectedApplication);
      } else if (task.id === 'translate_document') {
        if (uploadedFile) {
          // Handle file upload translation
          response = await taskApi.submitTranslateDocumentFileTask(
            uploadedFile,
            sourceLanguage,
            targetLanguage,
            outputFilename || undefined,
            outputFormat as 'txt' | 'docx' | 'json' | undefined
          );
        } else {
          // Handle text or patent translation
          response = await taskApi.submitTranslateDocumentTask({
            text_content: textInput || undefined,
            resource_id: resourceId || undefined,
            resource_type: selectedResourceType || undefined,
            source_language: sourceLanguage,
            target_language: targetLanguage,
          });
        }
      } else if (task.id === 'latex_to_docx') {
        if (!uploadedFile) {
          setError('Please upload a LaTeX document (.tex or .zip file)');
          setIsProcessing(false);
          return;
        }
        response = await taskApi.submitLatexConversionTask(uploadedFile, outputFilename || undefined);
      } else if (task.id === 'ocr_pdf') {
        if (!uploadedFile) {
          setError('Please upload a PDF document');
          setIsProcessing(false);
          return;
        }
        const ocrFilename = outputFilename || uploadedFile.name.replace(/\.pdf$/i, '');
        response = await taskApi.submitOcrPdfTask(uploadedFile, ocrFilename, outputFormat);
      } else if (task.id === 'custom_analysis') {
        // Handle different input modes for custom analysis
        if (customAnalysisInputMode === 'onboarded') {
          response = await taskApi.submitCustomAnalysisTask(
            resourceId,
            selectedResourceType,
            isApplication,
            textInput,
            'onboarded'
          );
        } else if (customAnalysisInputMode === 'raw_text') {
          response = await taskApi.submitCustomAnalysisTask(
            '',  // no resource ID for raw text
            '',  // no resource type
            false,  // not an application
            textInput,  // analysis instructions
            'raw_text',
            customAnalysisTextContent  // the raw text content
          );
        } else if (customAnalysisInputMode === 'upload') {
          if (!uploadedFile) {
            setError('Please upload a document');
            setIsProcessing(false);
            return;
          }
          response = await taskApi.submitCustomAnalysisTaskUpload(
            uploadedFile,
            textInput  // analysis instructions
          );
        }
      } else if (task.id === 'antecedent_basis_check') {
        // Validate input mode is selected
        if (!antecedentInputMode) {
          setError('Please select an input method');
          setIsProcessing(false);
          return;
        }

        // Handle different input types
        if (antecedentInputMode === 'onboarded') {
          if (!selectedResourceId) {
            setError('Please select a document');
            setIsProcessing(false);
            return;
          }
          // For patents and publications, extract the patent number
          if (selectedResourceType === 'patent' || selectedResourceType === 'publication') {
            response = await taskApi.submitAntecedentBasisCheck(
              'patent_number',
              selectedResourceId,
              '',  // custom_instruction
              outputFormat === 'json' ? 'json' : 'txt'
            );
          } else {
            // For uploads and other types, show error since backend needs file upload support for this
            setError('For uploaded documents, please use the "Upload a Document" option instead.');
            setIsProcessing(false);
            return;
          }
        } else if (antecedentInputMode === 'raw_text') {
          if (!textInput.trim()) {
            setError('Please enter claims text');
            setIsProcessing(false);
            return;
          }
          response = await taskApi.submitAntecedentBasisCheck(
            'raw_text',
            textInput,
            '',  // custom_instruction
            outputFormat === 'json' ? 'json' : 'txt'
          );
        } else if (antecedentInputMode === 'upload') {
          if (!uploadedFile) {
            setError('Please upload a document');
            setIsProcessing(false);
            return;
          }
          response = await taskApi.submitAntecedentBasisCheck(
            'upload',
            uploadedFile,
            '',  // custom_instruction
            outputFormat === 'json' ? 'json' : 'txt'
          );
        }
      } else if (task.id === 'technical_outline') {
        // Validate input mode is selected
        if (!technicalOutlineInputMode) {
          setError('Please select an input method');
          setIsProcessing(false);
          return;
        }

        // Handle different input types
        if (technicalOutlineInputMode === 'raw_text') {
          if (!textInput.trim()) {
            setError('Please enter disclosure text');
            setIsProcessing(false);
            return;
          }
          response = await taskApi.submitTechnicalOutline(
            'raw_text',
            textInput,
            '',  // custom_instruction
            outputFormat === 'json' ? 'json' : 'txt'
          );
        } else if (technicalOutlineInputMode === 'upload') {
          if (!uploadedFile) {
            setError('Please upload a document');
            setIsProcessing(false);
            return;
          }
          response = await taskApi.submitTechnicalOutline(
            'upload',
            uploadedFile,
            '',  // custom_instruction
            outputFormat === 'json' ? 'json' : 'txt'
          );
        }
      } else if (task.id === 'parts_process_outline') {
        // Validate input mode is selected
        if (!partsProcessOutlineInputMode) {
          setError('Please select an input method');
          setIsProcessing(false);
          return;
        }

        // Handle different input types
        if (partsProcessOutlineInputMode === 'raw_text') {
          if (!textInput.trim()) {
            setError('Please enter disclosure text');
            setIsProcessing(false);
            return;
          }
          response = await taskApi.submitPartsProcessOutline(
            'raw_text',
            textInput,
            '',  // custom_instruction
            outputFormat === 'json' ? 'json' : 'txt'
          );
        } else if (partsProcessOutlineInputMode === 'upload') {
          if (!uploadedFile) {
            setError('Please upload a document');
            setIsProcessing(false);
            return;
          }
          response = await taskApi.submitPartsProcessOutline(
            'upload',
            uploadedFile,
            '',  // custom_instruction
            outputFormat === 'json' ? 'json' : 'txt'
          );
        }
      } else {
        // For tasks not yet implemented
        setError('This task is not yet implemented in the API');
        setIsProcessing(false);
        return;
      }

      // Debug: Log the full response
      console.log('[DEBUG] API Response:', response);
      console.log('[DEBUG] Response type:', typeof response);
      console.log('[DEBUG] Is array?', Array.isArray(response));

      // Handle response that comes as array [errorObject, statusCode]
      let actualResponse = response;
      if (Array.isArray(response) && response.length > 0) {
        console.log('[DEBUG] Response is array, using first element');
        actualResponse = response[0];
      }

      // Check if response contains an error
      if (actualResponse && actualResponse.error) {
        const errorMsg = actualResponse.message || actualResponse.error;
        console.log('[DEBUG] Error detected:', errorMsg);
        throw new Error(errorMsg);
      }

      // Update response to use the actual response object
      response = actualResponse;

      // Check if we got a job_id
      if (response && response.job_id) {
        console.log('Task started with job_id:', response.job_id);

        // Start polling for results
        taskApi.startGraduatedPolling(
          async () => {
            const result = await taskApi.getJobResult(response.job_id);
            console.log('[POLL] Job ID:', response.job_id);
            console.log('[POLL] Result:', result);
            console.log('[POLL] Has analysis_id?', result && (result as any).analysis_id);

            // Check if we have a complete result (has analysis_id)
            if (result && (result as any).analysis_id) {
              console.log('[POLL] ✓ Analysis found, stopping polling');
              return result;
            }

            // Return null to continue polling
            console.log('[POLL] No analysis_id yet, continuing to poll...');
            return null;
          },
          async (result) => {
            console.log('Task completed successfully:', result);

            // Check if the result contains an error
            const analysisStr = typeof result.analysis === 'string' ? result.analysis : JSON.stringify(result.analysis);
            const hasError = analysisStr && (
              analysisStr.includes('[CREDIT ERROR]') ||
              analysisStr.includes('[ERROR]') ||
              analysisStr.includes('insufficient_credits')
            );

            if (hasError) {
              console.error('Task completed with error:', analysisStr);
              setError(analysisStr.includes('insufficient_credits')
                ? 'Insufficient credits. Please purchase more credits to continue.'
                : analysisStr);
              setIsProcessing(false);

              addNotification({
                type: 'error',
                title: 'Task Failed',
                message: analysisStr.includes('insufficient_credits')
                  ? 'Insufficient credits. Please purchase more credits to continue.'
                  : 'Task completed with an error. Please check the details.',
                duration: 8000,
              });
              return;
            }

            // Extract timestamp from various possible locations
            const timestamp = result.time_received ||
                            result.timestamp ||
                            result.analysis?.timestamp ||
                            new Date().toISOString();

            // Debug: Log timestamp source
            console.log('Timestamp debugging:', {
              task_type: task.id,
              time_received: result.time_received,
              timestamp: result.timestamp,
              analysis_timestamp: result.analysis?.timestamp,
              using: timestamp
            });

            const normalizedResult: any = {
              ...result,
              task_id: task.id,
              task_name: task.name,
              timestamp: timestamp,
              output: result.analysis,
              credits_used: result.credits_used,
              job_id: result.analysis_id,
            };

            if (!getDocumentIdFromResult(normalizedResult) && normalizedDocumentIdForRun) {
              normalizedResult.document_id = normalizedDocumentIdForRun;
            }

            setResult(normalizedResult);
            setIsProcessing(false);

            // Show success notification
            addNotification({
              type: 'success',
              title: 'Task Complete',
              message: `${task.name} has finished processing and is ready to view.`,
              duration: 5000,
            });

            // Dispatch event to update credits
            window.dispatchEvent(new Event('credits-updated'));

            // Reload task history to show the new result
            loadTaskHistory();
          },
          (error) => {
            console.error('Task polling error:', error);
            setError(error.message || 'Failed to retrieve task results');
            setIsProcessing(false);

            // Show error notification
            addNotification({
              type: 'error',
              title: 'Task Failed',
              message: error.message || `${task.name} failed to complete. Please try again.`,
              duration: 8000,
            });
          },
          (attempts, elapsedTime) => {
            console.log(`Polling attempt ${attempts}, elapsed time: ${elapsedTime}ms`);
          }
        );
      } else {
        throw new Error('No job ID returned from server');
      }
    } catch (err: any) {
      console.error('Error running task:', err);

      // Check if it's a credit error
      const errorMessage = err.message || err.error || 'Failed to execute task';
      const isInsufficientCredits = errorMessage.includes('insufficient_credits') ||
                                     errorMessage.includes('0 credits') ||
                                     errorMessage.includes('no credits');

      if (isInsufficientCredits) {
        setError('You have 0 credits. Please purchase more credits to continue.');
        addNotification({
          type: 'error',
          title: 'Insufficient Credits',
          message: 'You have 0 credits remaining. Please purchase more credits to continue using this feature.',
          duration: 10000,
        });
      } else {
        setError(errorMessage);
        addNotification({
          type: 'error',
          title: 'Task Failed',
          message: errorMessage,
          duration: 8000,
        });
      }

      setIsProcessing(false);
    }
  };

  const inputError = getTaskInputError();
  const runTaskLabel = ({ parts_list: 'Generate parts list', spec_map: 'Generate specification map', claims_support: 'Check claims support', claim_chart: 'Generate claim chart', claims_with_numerals: 'Add reference numerals', translate_document: 'Translate document', antecedent_basis_check: 'Check antecedent basis' } as Record<string, string>)[task.id] || `Run ${task.name}`;
  const selectedInputMode = task.id === 'translate_document' ? translationInputMode : task.id === 'antecedent_basis_check' ? antecedentInputMode : task.id === 'custom_analysis' ? customAnalysisInputMode : task.id.includes('ir_') ? irInputMode : task.id.includes('draft_') ? draftInputMode : task.id === 'technical_outline' ? technicalOutlineInputMode : task.id === 'parts_process_outline' ? partsProcessOutlineInputMode : null;
  const selectedInputDocument = availableDocuments.find(document => document.resource_id === selectedResourceId);
  const getInputSummary = () => {
    if (task.id.includes('oa_')) return task.id === 'oa_reporting_email' && oaReportingInputMode === 'upload' ? oaReportingFile?.name || 'Choose an office action PDF' : [selectedApplication && `Application ${selectedApplication}`, selectedOfficeAction].filter(Boolean).join(' · ') || 'Choose an application';
    if (selectedInputMode === 'upload') return uploadedFile?.name || 'Choose a file';
    if (selectedInputMode === 'raw_text' || selectedInputMode === 'text') return 'Text entered above';
    if (requiresDocumentSelection() && docInputMode === 'direct') return directPatentNumber.trim() || 'Enter a patent or publication number';
    if (selectedInputMode === 'onboarded' || requiresDocumentSelection()) return selectedInputDocument ? `${selectedInputDocument.file_name || 'Document'} · ${selectedInputDocument.resource_id}` : selectedResourceId || 'Choose a document';
    if (task.id === 'prior_art_analysis') return `${priorArtPubNumbers.split(/[\n,]+/).filter(value => value.trim()).length} prior art references`;
    if (task.id === 'patent_drawing') return patentDrawingFile?.name || 'Drawing instructions entered above';
    return uploadedFile?.name || patentNumber || applicationNumber || 'Text entered above';
  };

  return (
    // Two columns when there is width for them, stacked when there is not.
    //
    // A fixed 50/50 split meant that on a 1280px laptop — with the task list also taking
    // its 320px — the form and its results each got about 480px, and the results are
    // tables and claim text that do not survive that. Below `xl` they stack instead and
    // each takes half the height, which is the dimension there is more of.
    <div className="flex-1 flex flex-col xl:flex-row overflow-hidden bg-white h-full">
      {/* Left: Task Input Form */}
      <div className="min-h-0 flex-1 xl:flex-none xl:w-1/2 border-b xl:border-b-0 xl:border-r border-gray-200 flex flex-col xl:h-full">
        {/* Header */}
        <div className="flex-shrink-0 border-b border-gray-200 px-6 py-4">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3 flex-1">
              {CategoryIcon && (
                <div className="text-blue-500 text-xl mt-1">
                  <CategoryIcon className="w-6 h-6" />
                </div>
              )}
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h2 className="text-xl font-semibold">{task.name}</h2>
                  {task.coming_soon && (
                    <Badge variant="outline" className="bg-yellow-100 text-yellow-800 border-yellow-200">
                      Coming Soon
                    </Badge>
                  )}
                </div>
                <p className="text-sm text-gray-600 mt-1">{task.description}</p>
                <div className="flex items-center gap-2 mt-2">
                  <Badge variant="outline" className="bg-blue-100 text-blue-800 border-blue-200 text-xs">
                    {categoryInfo.name}
                  </Badge>
                  {/* Only on the tasks that actually draft prose. It is an account-wide
                      setting, so it is shown where it applies rather than everywhere. */}
                  {DRAFTING_TASK_IDS.has(task.id) && (
                    <ToolModelPicker setting="applicationModel" label="Drafting model" compact />
                  )}
                </div>
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              aria-label="Close task"
              className="flex-shrink-0"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>
        </div>

        {/* Task Input Form */}
        <div className="flex-1 overflow-y-auto">
          <div className="p-6 space-y-6">
              {/* Instructions */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-900">{getTaskInstructions()}</p>
              </div>

              {/* Application Selection for Office Action Tasks */}
              {task.id.includes('oa_') && !(task.id === 'oa_reporting_email' && oaReportingInputMode === 'upload') && (
                <div>
                  <Label htmlFor="application-select" className="text-sm font-medium mb-2 block">
                    Select Application
                  </Label>
                  <Select value={selectedApplication} onValueChange={setSelectedApplication}>
                    <SelectTrigger id="application-select">
                      <SelectValue placeholder="Select an application..." />
                    </SelectTrigger>
                    <SelectContent className="max-h-[300px] overflow-y-auto">
                      {applicationsLoading ? (
                        <SelectItem value="loading" disabled>
                          Loading applications...
                        </SelectItem>
                      ) : sessionApplications.length === 0 ? (
                        <SelectItem value="none" disabled>
                          No applications available. Add applications first.
                        </SelectItem>
                      ) : (
                        <>
                          {/* Active applications section */}
                          {sessionApplications.filter(app => app.isActive).length > 0 && (
                            <>
                              <div className="px-2 py-1.5 text-xs font-semibold text-gray-500 bg-gray-50">
                                Active Applications
                              </div>
                              {sessionApplications.filter(app => app.isActive).map((app) => (
                                <SelectItem key={app.application_id} value={app.application_id}>
                                  <div className="flex items-center gap-2">
                                    <CheckCircle2 className="w-3 h-3 text-green-600 flex-shrink-0" />
                                    <span>{app.title || app.application_id}</span>
                                  </div>
                                </SelectItem>
                              ))}
                            </>
                          )}

                          {/* Other applications section */}
                          {sessionApplications.filter(app => !app.isActive).length > 0 && (
                            <>
                              {sessionApplications.filter(app => app.isActive).length > 0 && (
                                <div className="px-2 py-1.5 text-xs font-semibold text-gray-500 bg-gray-50">
                                  Other Applications
                                </div>
                              )}
                              {sessionApplications.filter(app => !app.isActive).map((app) => (
                                <SelectItem key={app.application_id} value={app.application_id}>
                                  {app.title || app.application_id}
                                </SelectItem>
                              ))}
                            </>
                          )}
                        </>
                      )}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500 mt-1">
                    Select the application to analyze
                  </p>
                </div>
              )}

              {/* Office Action Document Selection (for tasks other than timeline) */}
              {requiresOfficeActionDocument() && selectedApplication && (
                <div>
                  <Label htmlFor="oa-document-select" className="text-sm font-medium mb-2 block">
                    Select Office Action
                  </Label>
                  <Select value={selectedOfficeAction} onValueChange={setSelectedOfficeAction}>
                    <SelectTrigger id="oa-document-select">
                      <SelectValue placeholder="Select an office action..." />
                    </SelectTrigger>
                    <SelectContent className="max-h-[300px] overflow-y-auto">
                      {prosecutionDocsLoading ? (
                        <SelectItem value="loading" disabled>
                          Loading office actions...
                        </SelectItem>
                      ) : prosecutionDocs.length === 0 ? (
                        <SelectItem value="none" disabled>
                          No office actions found for this application.
                        </SelectItem>
                      ) : (
                        prosecutionDocs.map((doc) => (
                          <SelectItem key={doc.docId} value={doc.docId}>
                            <div className="flex flex-col">
                              <span>{doc.description}</span>
                              <span className="text-xs text-gray-500">{doc.date}</span>
                            </div>
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500 mt-1">
                    Select the office action document to analyze
                  </p>
                </div>
              )}

              {/* OA Reporting Email — Input Mode Toggle */}
              {task.id === 'oa_reporting_email' && (
                <div className="space-y-3">
                  <Label className="text-sm font-medium">Office Action Source</Label>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant={oaReportingInputMode === 'onboarded' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => { setOaReportingInputMode('onboarded'); setOaReportingFile(null); }}
                      className="flex-1"
                      disabled={isProcessing}
                    >
                      Select from Case
                    </Button>
                    <Button
                      type="button"
                      variant={oaReportingInputMode === 'upload' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setOaReportingInputMode('upload')}
                      className="flex-1"
                      disabled={isProcessing}
                    >
                      Upload PDF
                    </Button>
                  </div>
                  {oaReportingInputMode === 'upload' && (
                    <div className="space-y-3">
                      <input
                        type="file"
                        accept=".pdf"
                        id="oa-reporting-file-input"
                        className="hidden"
                        onChange={(e) => setOaReportingFile(e.target.files?.[0] || null)}
                        disabled={isProcessing}
                      />
                      <label
                        htmlFor="oa-reporting-file-input"
                        className={`flex items-center justify-center gap-2 w-full border-2 border-dashed rounded-lg p-4 cursor-pointer transition-colors ${
                          oaReportingFile
                            ? 'border-green-400 bg-green-50 text-green-700'
                            : 'border-gray-300 bg-gray-50 text-gray-500 hover:border-gray-400 hover:bg-gray-100'
                        } ${isProcessing ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        {oaReportingFile ? (
                          <span className="text-sm font-medium truncate">{oaReportingFile.name}</span>
                        ) : (
                          <span className="text-sm">Click to upload Office Action PDF</span>
                        )}
                      </label>
                      {oaReportingFile && (
                        <div className="space-y-1">
                          <Label className="text-xs font-medium text-gray-700">Action Type</Label>
                          <Select value={oaUploadActionType} onValueChange={setOaUploadActionType} disabled={isProcessing}>
                            <SelectTrigger className="h-8 text-xs">
                              <SelectValue placeholder="Select action type…" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="CTFR">Non-Final Office Action</SelectItem>
                              <SelectItem value="CTNF">Final Office Action</SelectItem>
                              <SelectItem value="RSTRT">Restriction / Election</SelectItem>
                              <SelectItem value="NOA">Notice of Allowance</SelectItem>
                              <SelectItem value="CTEQ">Ex Parte Quayle</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Email Template Selection for OA Reporting Email */}
              {task.id === 'oa_reporting_email' && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label htmlFor="email-template-select" className="text-sm font-medium">
                      Email Template
                    </Label>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setShowTemplateManager(true)}
                      className="text-xs"
                    >
                      Manage Templates
                    </Button>
                  </div>
                  <Select value={selectedEmailTemplate} onValueChange={setSelectedEmailTemplate}>
                    <SelectTrigger id="email-template-select" className="truncate">
                      <SelectValue placeholder="Select an email template...">
                        {(() => {
                          // Find selected template and show only its name (truncated)
                          const customTemplate = userEmailTemplates.find((t: any) => t.prompt_id === selectedEmailTemplate);
                          if (customTemplate) {
                            return <span className="truncate">{customTemplate.name}</span>;
                          }
                          const defaultTemplate = OA_EMAIL_TEMPLATES.find(t => t.id === selectedEmailTemplate);
                          if (defaultTemplate) {
                            return <span className="truncate">{defaultTemplate.name}</span>;
                          }
                          return "Select an email template...";
                        })()}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent className="max-h-[300px] overflow-y-auto">
                      {/* User Custom Templates - shown first */}
                      {userEmailTemplates.length > 0 && (
                        <>
                          <div className="px-2 py-1.5 text-xs font-semibold text-gray-500 bg-gray-50">
                            My Custom Templates
                          </div>
                          {userEmailTemplates.map((template: any) => (
                            <SelectItem key={template.prompt_id} value={template.prompt_id}>
                              <div className="flex flex-col">
                                <span className="font-medium">{template.name}</span>
                                <span className="text-xs text-gray-500">
                                  {template.description || 'Custom template'}
                                </span>
                              </div>
                            </SelectItem>
                          ))}
                        </>
                      )}

                      {/* Default Templates */}
                      <div className={`px-2 py-1.5 text-xs font-semibold text-gray-500 bg-gray-50 ${userEmailTemplates.length > 0 ? 'border-t' : ''}`}>
                        Default Templates
                      </div>
                      {OA_EMAIL_TEMPLATES.map((template) => (
                        <SelectItem key={template.id} value={template.id}>
                          <div className="flex flex-col">
                            <span className="font-medium">{template.name}</span>
                            <span className="text-xs text-gray-500">{template.description}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500 mt-1">
                    Choose the email format and tone
                  </p>
                </div>
              )}

              {/* Custom Analysis Input Mode Selection */}
              {task.id === 'custom_analysis' && !customAnalysisInputMode && (
                <div className="space-y-3">
                  <Label className="text-sm font-medium">Choose Input Method</Label>
                  <div className="grid grid-cols-1 gap-3">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setCustomAnalysisInputMode('onboarded')}
                      className="h-auto py-4 flex flex-col items-center gap-2 hover:bg-blue-50 hover:border-blue-500"
                    >
                      <FileText className="w-6 h-6 text-blue-600" />
                      <div className="text-center">
                        <div className="font-medium">Select an Onboarded Document</div>
                        <div className="text-xs text-gray-500 mt-1">Choose from patents, publications, or uploads</div>
                      </div>
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        if (!isPaid) {
                          addNotification({
                            type: 'warning',
                            title: 'Pro or Premium Required',
                            message: 'File uploads are available for Pro and Premium subscribers. Please upgrade to access this feature.',
                            duration: 6000,
                          });
                          return;
                        }
                        setCustomAnalysisInputMode('upload');
                      }}
                      disabled={!isPaid}
                      className={`h-auto py-4 flex flex-col items-center gap-2 ${
                        !isPaid
                          ? 'opacity-60 cursor-not-allowed'
                          : 'hover:bg-blue-50 hover:border-blue-500'
                      }`}
                    >
                      <Upload className={`w-6 h-6 ${!isPaid ? 'text-gray-400' : 'text-blue-600'}`} />
                      <div className="text-center">
                        <div className="flex items-center gap-2 justify-center">
                          <span className={`font-medium ${!isPaid ? 'text-gray-400' : ''}`}>Upload a Document</span>
                          {!isPaid && (
                            <Badge variant="outline" className="bg-purple-50 text-purple-800 border-purple-200 text-xs">
                              <Crown className="w-3 h-3 mr-1 inline" />
                              Pro/Premium
                            </Badge>
                          )}
                        </div>
                        <div className={`text-xs mt-1 ${!isPaid ? 'text-gray-400' : 'text-gray-500'}`}>Upload PDF, DOCX, TXT, or DOC file</div>
                      </div>
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setCustomAnalysisInputMode('raw_text')}
                      className="h-auto py-4 flex flex-col items-center gap-2 hover:bg-blue-50 hover:border-blue-500"
                    >
                      <MessageSquarePlus className="w-6 h-6 text-blue-600" />
                      <div className="text-center">
                        <div className="font-medium">Enter Raw Text</div>
                        <div className="text-xs text-gray-500 mt-1">Type or paste text content directly</div>
                      </div>
                    </Button>
                  </div>
                </div>
              )}

              {/* Document Selection for Custom Analysis (Onboarded Mode) */}
              {task.id === 'custom_analysis' && customAnalysisInputMode === 'onboarded' && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between mb-2">
                    <Label htmlFor="custom-analysis-doc-select" className="text-sm font-medium">
                      Select Document to Analyze
                    </Label>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setCustomAnalysisInputMode(null);
                        setSelectedResourceId('');
                        setSelectedResourceType('');
                        setDocumentSearchQuery('');
                        setSearchResults([]);
                      }}
                      className="h-6 text-xs text-gray-500 hover:text-gray-700"
                    >
                      Change Input Method
                    </Button>
                  </div>

                  {/* Search Input */}
                  <div className="space-y-2">
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                        <Input
                          type="text"
                          placeholder="Search documents by name, ID, or title..."
                          value={documentSearchQuery}
                          onChange={(e) => setDocumentSearchQuery(e.target.value)}
                          className="pl-10 pr-10"
                          disabled={isSearching}
                        />
                        {documentSearchQuery && !isSearching && (
                          <button
                            onClick={() => {
                              setDocumentSearchQuery('');
                              setSearchResults([]);
                            }}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                            type="button"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          if (documentSearchQuery.trim().length >= 2) {
                            setDocumentSearchQuery(documentSearchQuery + ' ');
                            setTimeout(() => setDocumentSearchQuery(documentSearchQuery.trim()), 0);
                          }
                        }}
                        disabled={isSearching || documentSearchQuery.trim().length < 2}
                        className="px-4"
                      >
                        {isSearching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>

                  {/* Document Selector */}
                  <Select value={selectedResourceId} onValueChange={(value) => {
                    setSelectedResourceId(value);
                    const docsToSearch = documentSearchQuery ? searchResults : availableDocuments;
                    const selectedDoc = docsToSearch.find(doc => doc.resource_id === value);
                    if (selectedDoc) {
                      setSelectedResourceType(selectedDoc.file_type);
                    }
                  }}>
                    <SelectTrigger id="custom-analysis-doc-select">
                      <SelectValue placeholder="Select a document" />
                    </SelectTrigger>
                    <SelectContent>
                      <ScrollArea className="max-h-[300px]">
                        {(documentSearchQuery ? searchResults : availableDocuments).map((doc) => (
                          <SelectItem key={doc.resource_id} value={doc.resource_id}>
                            <div className="flex items-center gap-2">
                              <FileText className="w-4 h-4" />
                              <span>{doc.file_name || doc.resource_id}</span>
                              {doc.isActive && <Badge variant="secondary" className="text-[10px]">Active</Badge>}
                            </div>
                          </SelectItem>
                        ))}
                      </ScrollArea>
                    </SelectContent>
                  </Select>

                  <p className="text-xs text-gray-500">
                    Select a document to analyze with your custom instructions
                  </p>
                </div>
              )}

              {/* Raw Text Input for Custom Analysis */}
              {task.id === 'custom_analysis' && customAnalysisInputMode === 'raw_text' && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between mb-2">
                    <Label htmlFor="custom-analysis-text" className="text-sm font-medium">
                      Text Content
                    </Label>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setCustomAnalysisInputMode(null);
                        setCustomAnalysisTextContent('');
                        setTextInput('');
                      }}
                      className="h-6 text-xs text-gray-500 hover:text-gray-700"
                    >
                      Change Input Method
                    </Button>
                  </div>
                  <Textarea
                    id="custom-analysis-text"
                    value={customAnalysisTextContent}
                    onChange={(e) => setCustomAnalysisTextContent(e.target.value)}
                    placeholder="Enter the text content you want to analyze..."
                    className="min-h-[200px]"
                    disabled={isProcessing}
                  />
                </div>
              )}

              {/* File Upload for Custom Analysis */}
              {task.id === 'custom_analysis' && customAnalysisInputMode === 'upload' && (
                <div className="space-y-4">
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label htmlFor="custom-analysis-file" className="text-sm font-medium">
                        Upload Document
                      </Label>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setCustomAnalysisInputMode(null);
                          setUploadedFile(null);
                          const fileInput = document.getElementById('custom-analysis-file') as HTMLInputElement;
                          if (fileInput) fileInput.value = '';
                        }}
                        className="h-6 text-xs text-gray-500 hover:text-gray-700"
                      >
                        Change Input Method
                      </Button>
                    </div>

                    <div
                      onDragOver={(e) => {
                        e.preventDefault();
                        setIsDragOver(true);
                      }}
                      onDragLeave={() => setIsDragOver(false)}
                      onDrop={(e) => {
                        e.preventDefault();
                        setIsDragOver(false);
                        const file = e.dataTransfer.files?.[0];
                        if (file) {
                          setUploadedFile(file);
                          setError('');
                        }
                      }}
                      className={`border-2 border-dashed rounded-lg p-6 text-center ${
                        isDragOver ? 'border-blue-400 bg-blue-50' : 'border-gray-300 bg-gray-50'
                      }`}
                    >
                      <input
                        id="custom-analysis-file"
                        type="file"
                        accept=".pdf,.docx,.txt,.doc"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            setUploadedFile(file);
                            setError('');
                          }
                        }}
                        className="hidden"
                        disabled={isProcessing}
                      />
                      <label htmlFor="custom-analysis-file" className="cursor-pointer">
                        <Upload className="w-12 h-12 mx-auto text-gray-400 mb-3" />
                        <p className="text-sm text-gray-600">
                          {isDragOver ? 'Drop document here' : 'Drag and drop your document'}
                        </p>
                        <p className="text-xs text-gray-500 mt-2">PDF, DOCX, TXT, or DOC</p>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="mt-3"
                          onClick={(e) => {
                            e.preventDefault();
                            document.getElementById('custom-analysis-file')?.click();
                          }}
                        >
                          Browse Files
                        </Button>
                      </label>
                    </div>

                    {uploadedFile && (
                      <div className="mt-3 flex items-center justify-between p-3 bg-white border rounded-lg">
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4" />
                          <span className="text-sm">{uploadedFile.name}</span>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setUploadedFile(null);
                            const fileInput = document.getElementById('custom-analysis-file') as HTMLInputElement;
                            if (fileInput) fileInput.value = '';
                          }}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Analysis Instructions for Custom Analysis (all modes) */}
              {task.id === 'custom_analysis' && customAnalysisInputMode && (
                <div>
                  <Label htmlFor="custom-analysis-instructions" className="text-sm font-medium mb-2 block">
                    Analysis Instructions
                  </Label>
                  <Textarea
                    id="custom-analysis-instructions"
                    value={textInput}
                    onChange={(e) => setTextInput(e.target.value)}
                    placeholder="Describe what analysis or information you need (e.g., 'Compare the claims to identify overlapping scope' or 'Summarize the key technical advantages')..."
                    className="min-h-[100px]"
                    disabled={isProcessing}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Provide specific instructions for your custom analysis
                  </p>
                </div>
              )}

              {/* Input Mode Selection for IR Tasks */}
              {task.id.includes('ir_') && !irInputMode && (
                <div className="space-y-3">
                  <Label className="text-sm font-medium">Choose Input Method</Label>
                  <div className="grid grid-cols-1 gap-3">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setIrInputMode('onboarded')}
                      className="h-auto py-4 flex flex-col items-center gap-2 hover:bg-blue-50 hover:border-blue-500"
                    >
                      <FileText className="w-6 h-6 text-blue-600" />
                      <div className="text-center">
                        <div className="font-medium">Select an Onboarded Document</div>
                        <div className="text-xs text-gray-500 mt-1">Choose from patents, publications, or uploads</div>
                      </div>
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        if (!isPaid) {
                          addNotification({
                            type: 'warning',
                            title: 'Pro or Premium Required',
                            message: 'File uploads are available for Pro and Premium subscribers. Please upgrade to access this feature.',
                            duration: 6000,
                          });
                          return;
                        }
                        setIrInputMode('upload');
                      }}
                      disabled={!isPaid}
                      className={`h-auto py-4 flex flex-col items-center gap-2 ${
                        !isPaid
                          ? 'opacity-60 cursor-not-allowed'
                          : 'hover:bg-blue-50 hover:border-blue-500'
                      }`}
                    >
                      <Upload className={`w-6 h-6 ${!isPaid ? 'text-gray-400' : 'text-blue-600'}`} />
                      <div className="text-center">
                        <div className="flex items-center gap-2 justify-center">
                          <span className={`font-medium ${!isPaid ? 'text-gray-400' : ''}`}>Upload a Document</span>
                          {!isPaid && (
                            <Badge variant="outline" className="bg-purple-50 text-purple-800 border-purple-200 text-xs">
                              <Crown className="w-3 h-3 mr-1 inline" />
                              Pro/Premium
                            </Badge>
                          )}
                        </div>
                        <div className={`text-xs mt-1 ${!isPaid ? 'text-gray-400' : 'text-gray-500'}`}>Upload PDF, DOCX, TXT, or DOC file</div>
                      </div>
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setIrInputMode('raw_text')}
                      className="h-auto py-4 flex flex-col items-center gap-2 hover:bg-blue-50 hover:border-blue-500"
                    >
                      <MessageSquarePlus className="w-6 h-6 text-blue-600" />
                      <div className="text-center">
                        <div className="font-medium">Enter Raw Text</div>
                        <div className="text-xs text-gray-500 mt-1">Type or paste invention record text directly</div>
                      </div>
                    </Button>
                  </div>
                </div>
              )}

              {/* Document Selection for IR Tasks (Onboarded Mode) */}
              {task.id.includes('ir_') && irInputMode === 'onboarded' && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between mb-2">
                    <Label htmlFor="ir-doc-select" className="text-sm font-medium">
                      Select Document
                    </Label>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setIrInputMode(null);
                        setSelectedResourceId('');
                        setSelectedResourceType('');
                      }}
                      className="h-6 text-xs text-gray-500 hover:text-gray-700"
                    >
                      Change Input Method
                    </Button>
                  </div>
                  <Select value={selectedResourceId} onValueChange={(value) => {
                    setSelectedResourceId(value);
                    const selectedDoc = availableDocuments.find((doc: any) => doc.resource_id === value);
                    if (selectedDoc) {
                      setSelectedResourceType(selectedDoc.file_type);
                    }
                    setError('');
                  }}>
                    <SelectTrigger id="ir-doc-select">
                      <SelectValue placeholder="Select a document" />
                    </SelectTrigger>
                    <SelectContent>
                      <ScrollArea className="h-[200px]">
                        {availableDocuments.map((doc: any) => (
                          <SelectItem key={doc.resource_id} value={doc.resource_id}>
                            <div className="flex items-center gap-2">
                              <Badge variant="secondary" className="text-[10px]">{doc.file_type}</Badge>
                              <span>{doc.file_name || doc.resource_id}</span>
                              {doc.isActive && <Badge variant="secondary" className="text-[10px]">Active</Badge>}
                            </div>
                          </SelectItem>
                        ))}
                      </ScrollArea>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500">
                    Select a document to extract invention record information from
                  </p>
                </div>
              )}

              {/* Raw Text Input for IR Tasks */}
              {task.id.includes('ir_') && irInputMode === 'raw_text' && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between mb-2">
                    <Label htmlFor="ir-text" className="text-sm font-medium">
                      Invention Record Text
                    </Label>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setIrInputMode(null);
                        setIrTextContent('');
                      }}
                      className="h-6 text-xs text-gray-500 hover:text-gray-700"
                    >
                      Change Input Method
                    </Button>
                  </div>
                  <Textarea
                    id="ir-text"
                    value={irTextContent}
                    onChange={(e) => setIrTextContent(e.target.value)}
                    placeholder="Enter your invention record text here..."
                    className="min-h-[200px]"
                    disabled={isProcessing}
                  />
                </div>
              )}

              {/* File Upload for IR Tasks */}
              {task.id.includes('ir_') && irInputMode === 'upload' && (
                <div className="space-y-4">
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label htmlFor="ir-file" className="text-sm font-medium">
                        Upload Document
                      </Label>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setIrInputMode(null);
                          setUploadedFile(null);
                          const fileInput = document.getElementById('ir-file') as HTMLInputElement;
                          if (fileInput) fileInput.value = '';
                        }}
                        className="h-6 text-xs text-gray-500 hover:text-gray-700"
                      >
                        Change Input Method
                      </Button>
                    </div>

                    <div
                      onDragOver={(e) => {
                        e.preventDefault();
                        setIsDragOver(true);
                      }}
                      onDragLeave={() => setIsDragOver(false)}
                      onDrop={(e) => {
                        e.preventDefault();
                        setIsDragOver(false);
                        const file = e.dataTransfer.files?.[0];
                        if (file) {
                          setUploadedFile(file);
                          setError('');
                        }
                      }}
                      className={`border-2 border-dashed rounded-lg p-6 text-center ${
                        isDragOver ? 'border-blue-400 bg-blue-50' : 'border-gray-300 bg-gray-50'
                      }`}
                    >
                      <input
                        id="ir-file"
                        type="file"
                        accept=".pdf,.docx,.txt,.doc"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            setUploadedFile(file);
                            setError('');
                          }
                        }}
                        className="hidden"
                        disabled={isProcessing}
                      />
                      <label htmlFor="ir-file" className="cursor-pointer">
                        <Upload className="w-12 h-12 mx-auto text-gray-400 mb-3" />
                        <p className="text-sm text-gray-600">
                          {isDragOver ? 'Drop document here' : 'Drag and drop your document'}
                        </p>
                        <p className="text-xs text-gray-500 mt-2">PDF, DOCX, TXT, or DOC</p>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="mt-3"
                          onClick={(e) => {
                            e.preventDefault();
                            document.getElementById('ir-file')?.click();
                          }}
                        >
                          Browse Files
                        </Button>
                      </label>
                    </div>

                    {uploadedFile && (
                      <div className="mt-3 flex items-center justify-between p-3 bg-white border rounded-lg">
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4" />
                          <span className="text-sm">{uploadedFile.name}</span>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setUploadedFile(null);
                            const fileInput = document.getElementById('ir-file') as HTMLInputElement;
                            if (fileInput) fileInput.value = '';
                          }}
                        >
                          Remove
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Document Input Method Selection for Tasks that Require It */}
              {requiresDocumentSelection() && !task.id.includes('oa_') && task.id !== 'custom_analysis' && !task.id.includes('draft_') && docInputMode === 'dropdown' && !selectedResourceId && !choosingExistingDocument && (
                <div className="space-y-3">
                  <Label className="text-sm font-medium">Choose Input Method</Label>
                  <div className="grid grid-cols-1 gap-3">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setDocInputMode('dropdown');
                        setChoosingExistingDocument(true);
                        const document = selectTaskDocument(availableDocuments, preferredDocument);
                        if (document) {
                          setSelectedResourceId(document.resource_id);
                          setSelectedResourceType(document.file_type);
                        }
                      }}
                      className="h-auto py-4 flex flex-col items-center gap-2 hover:bg-blue-50 hover:border-blue-500"
                    >
                      <FileText className="w-6 h-6 text-blue-600" />
                      <div className="text-center">
                        <div className="font-medium">Select an Existing Document</div>
                        <div className="text-xs text-gray-500 mt-1">Choose from your onboarded patents and publications</div>
                      </div>
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setDocInputMode('direct');
                        setSelectedResourceId('');
                        setSelectedResourceType('');
                      }}
                      className="h-auto py-4 flex flex-col items-center gap-2 hover:bg-blue-50 hover:border-blue-500"
                    >
                      <Search className="w-6 h-6 text-blue-600" />
                      <div className="text-center">
                        <div className="font-medium">Enter a New Patent/Publication Number</div>
                        <div className="text-xs text-gray-500 mt-1">Type a patent, publication, or application number to fetch automatically</div>
                      </div>
                    </Button>
                  </div>
                </div>
              )}

              {/* Existing Document Dropdown (after selecting "Existing Document" mode) */}
              {requiresDocumentSelection() && !task.id.includes('oa_') && task.id !== 'custom_analysis' && !task.id.includes('draft_') && docInputMode === 'dropdown' && (selectedResourceId || choosingExistingDocument) && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between mb-2">
                    <Label htmlFor="document-select" className="text-sm font-medium">
                      Select Document
                    </Label>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setDocInputMode('dropdown');
                        setChoosingExistingDocument(false);
                        setSelectedResourceId('');
                        setSelectedResourceType('');
                      }}
                    >
                      Change Input Method
                    </Button>
                  </div>
                  <Select value={selectedResourceId} onValueChange={(value) => {
                    setSelectedResourceId(value);
                    const selectedDoc = availableDocuments.find(
                      (doc) => doc.resource_id === value
                    );
                    if (selectedDoc) {
                      setSelectedResourceType(selectedDoc.file_type);
                    }
                  }}>
                    <SelectTrigger id="document-select">
                      <SelectValue placeholder="Select a patent or publication..." />
                    </SelectTrigger>
                    <SelectContent className="max-h-[300px] overflow-y-auto">
                      {documentsLoading ? (
                        <SelectItem value="loading" disabled>
                          Loading documents...
                        </SelectItem>
                      ) : availableDocuments.length === 0 ? (
                        <SelectItem value="none" disabled>
                          No documents available. Add documents first.
                        </SelectItem>
                      ) : (
                        <>
                          {availableDocuments.filter(doc => doc.isActive).length > 0 && (
                            <>
                              <div className="px-2 py-1.5 text-xs font-semibold text-gray-500 bg-gray-50">
                                Session documents
                              </div>
                              {availableDocuments.filter(doc => doc.isActive).map((doc) => {
                                const docId = doc.resource_id;
                                const docName = doc.file_name || docId;
                                const docType = doc.file_type;
                                return (
                                  <SelectItem key={docId} value={docId}>
                                    <div className="flex items-center gap-2">
                                      <span className="text-blue-500" title="Active in current session">
                                        <CheckCircle2 className="w-3 h-3 text-green-600 flex-shrink-0" />
                                      </span>
                                      <span>{docName}</span>
                                      <span className="text-xs text-gray-500">({docType})</span>
                                    </div>
                                  </SelectItem>
                                );
                              })}
                            </>
                          )}
                          {availableDocuments.filter(doc => !doc.isActive).length > 0 && (
                            <>
                              {availableDocuments.filter(doc => doc.isActive).length > 0 && (
                                <div className="px-2 py-1.5 text-xs font-semibold text-gray-500 bg-gray-50">
                                  Other Documents
                                </div>
                              )}
                              {availableDocuments.filter(doc => !doc.isActive).map((doc) => {
                                const docId = doc.resource_id;
                                const docName = doc.file_name || docId;
                                const docType = doc.file_type;
                                return (
                                  <SelectItem key={docId} value={docId}>
                                    <div className="flex items-center gap-2">
                                      <span>{docName}</span>
                                      <span className="text-xs text-gray-500">({docType})</span>
                                    </div>
                                  </SelectItem>
                                );
                              })}
                            </>
                          )}
                        </>
                      )}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500 mt-1">
                    {availableDocuments.length === 0
                      ? 'Add patents or publications from the Documents page first'
                      : 'Select a patent or publication to analyze.'}
                  </p>
                </div>
              )}

              {/* Direct Patent Number Entry (after selecting "New Document" mode) */}
              {requiresDocumentSelection() && !task.id.includes('oa_') && task.id !== 'custom_analysis' && !task.id.includes('draft_') && docInputMode === 'direct' && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-sm font-medium">
                      Enter Patent/Publication Number
                    </Label>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setDocInputMode('dropdown');
                        setSelectedResourceId('');
                        setSelectedResourceType('');
                        setDirectPatentNumber('');
                        setDetectedDocType(null);
                        setDirectInputError('');
                      }}
                    >
                      Change Input Method
                    </Button>
                  </div>
                  <Input
                    placeholder="e.g. 9424461, US20240123456A1, 17/234,567, EP1234567A1"
                    value={directPatentNumber}
                    onChange={(e) => {
                      const val = e.target.value;
                      setDirectPatentNumber(val);
                      setDirectInputError('');
                      if (!val.trim()) {
                        setDetectedDocType(null);
                        return;
                      }
                      const classified = classifyPatentNumber(val);
                      if (classified) {
                        setDetectedDocType(classified.displayLabel);
                        setDirectInputError('');
                      } else {
                        setDetectedDocType(null);
                        setDirectInputError('Could not recognize format. Try: 9424461, US20240123456A1, 17/234,567, or EP1234567A1');
                      }
                    }}
                  />
                  {detectedDocType && (
                    <div className="flex items-center gap-1.5">
                      <CheckCircle2 className="w-3.5 h-3.5 text-green-600" />
                      <span className="text-xs text-green-700">Detected: {detectedDocType}</span>
                    </div>
                  )}
                  {directInputError && (
                    <p className="text-xs text-red-600">{directInputError}</p>
                  )}
                  <p className="text-xs text-gray-500">
                    Enter a US patent, publication, application, or foreign patent number. The document will be fetched automatically.
                  </p>
                </div>
              )}

              {/* Claims Support Options */}
              {hasClaimsSupportOptions() && (selectedResourceId || (docInputMode === 'direct' && detectedDocType)) && (
                <div className="space-y-2">
                  {task.resource_interface?.input?.use_entire_description && (
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="use-entire-description"
                        checked={useEntireDescription}
                        onCheckedChange={(checked) => setUseEntireDescription(checked as boolean)}
                      />
                      <Label
                        htmlFor="use-entire-description"
                        className="text-sm font-normal cursor-pointer"
                      >
                        Use entire description
                      </Label>
                    </div>
                  )}
                  {task.resource_interface?.input?.use_entire_claims && (
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="use-entire-claims"
                        checked={useEntireClaims}
                        onCheckedChange={(checked) => setUseEntireClaims(checked as boolean)}
                      />
                      <Label
                        htmlFor="use-entire-claims"
                        className="text-sm font-normal cursor-pointer"
                      >
                        Use entire claims
                      </Label>
                    </div>
                  )}
                  {task.resource_interface?.input?.include_support && (
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="include-support"
                        checked={includeSupport}
                        onCheckedChange={(checked) => setIncludeSupport(checked as boolean)}
                      />
                      <Label
                        htmlFor="include-support"
                        className="text-sm font-normal cursor-pointer"
                      >
                        Include support column
                      </Label>
                    </div>
                  )}
                </div>
              )}

              {/* Translation Task Input Mode Selection */}
              {task.id === 'translate_document' && !translationInputMode && (
                <div className="space-y-3">
                  <Label className="text-sm font-medium">Choose Input Method</Label>
                  <div className="grid grid-cols-1 gap-3">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setTranslationInputMode('onboarded')}
                      className="h-auto py-4 flex flex-col items-center gap-2 hover:bg-blue-50 hover:border-blue-500"
                    >
                      <FileText className="w-6 h-6 text-blue-600" />
                      <div className="text-center">
                        <div className="font-medium">Select an Onboarded Document</div>
                        <div className="text-xs text-gray-500 mt-1">Choose from patents, publications, or uploads</div>
                      </div>
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        if (!isPaid) {
                          addNotification({
                            type: 'warning',
                            title: 'Pro or Premium Required',
                            message: 'File uploads are available for Pro and Premium subscribers. Please upgrade to access this feature.',
                            duration: 6000,
                          });
                          return;
                        }
                        setTranslationInputMode('upload');
                      }}
                      disabled={!isPaid}
                      className={`h-auto py-4 flex flex-col items-center gap-2 ${
                        !isPaid
                          ? 'opacity-60 cursor-not-allowed'
                          : 'hover:bg-blue-50 hover:border-blue-500'
                      }`}
                    >
                      <Upload className={`w-6 h-6 ${!isPaid ? 'text-gray-400' : 'text-blue-600'}`} />
                      <div className="text-center">
                        <div className="flex items-center gap-2 justify-center">
                          <span className={`font-medium ${!isPaid ? 'text-gray-400' : ''}`}>Upload a Document</span>
                          {!isPaid && (
                            <Badge variant="outline" className="bg-purple-50 text-purple-800 border-purple-200 text-xs">
                              <Crown className="w-3 h-3 mr-1 inline" />
                              Pro/Premium
                            </Badge>
                          )}
                        </div>
                        <div className={`text-xs mt-1 ${!isPaid ? 'text-gray-400' : 'text-gray-500'}`}>Upload PDF, DOCX, or TXT file</div>
                      </div>
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setTranslationInputMode('text')}
                      className="h-auto py-4 flex flex-col items-center gap-2 hover:bg-blue-50 hover:border-blue-500"
                    >
                      <MessageSquarePlus className="w-6 h-6 text-blue-600" />
                      <div className="text-center">
                        <div className="font-medium">Enter Raw Text</div>
                        <div className="text-xs text-gray-500 mt-1">Type or paste text to translate</div>
                      </div>
                    </Button>
                  </div>
                </div>
              )}

              {/* Document Selection for Translation (Onboarded Mode) */}
              {task.id === 'translate_document' && translationInputMode === 'onboarded' && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between mb-2">
                    <Label htmlFor="translation-doc-select" className="text-sm font-medium">
                      Select Document to Translate
                    </Label>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setTranslationInputMode(null);
                        setSelectedResourceId('');
                        setSelectedResourceType('');
                        setDocumentSearchQuery('');
                        setSearchResults([]);
                      }}
                      className="h-6 text-xs text-gray-500 hover:text-gray-700"
                    >
                      Change Input Method
                    </Button>
                  </div>

                  {/* Search Input */}
                  <div className="space-y-2">
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                        <Input
                          type="text"
                          placeholder="Search documents by name, ID, or title... (press Enter)"
                          value={documentSearchQuery}
                          onChange={(e) => setDocumentSearchQuery(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              // Search is already triggered by useEffect with debounce
                            }
                          }}
                          className="pl-10 pr-10"
                          disabled={isSearching}
                        />
                        {documentSearchQuery && !isSearching && (
                          <button
                            onClick={() => {
                              setDocumentSearchQuery('');
                              setSearchResults([]);
                            }}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 pointer-events-auto"
                            type="button"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          // Trigger immediate search
                          if (documentSearchQuery.trim().length >= 2) {
                            setDocumentSearchQuery(documentSearchQuery + ' '); // Force re-trigger
                            setTimeout(() => setDocumentSearchQuery(documentSearchQuery.trim()), 0);
                          }
                        }}
                        disabled={isSearching || documentSearchQuery.trim().length < 2}
                        className="px-4"
                      >
                        {isSearching ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Search className="w-4 h-4" />
                        )}
                      </Button>
                    </div>

                    {/* Search Results Info */}
                    {documentSearchQuery.trim().length >= 2 && !isSearching && (
                      <div className="text-xs text-gray-600">
                        {searchResults.length > 0 ? (
                          <span className="text-green-700">
                            ✓ Found {searchResults.length} match{searchResults.length !== 1 ? 'es' : ''}
                          </span>
                        ) : (
                          <span className="text-amber-700">No documents found matching "{documentSearchQuery}"</span>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Document Selector */}
                  <Select value={selectedResourceId} onValueChange={(value) => {
                    setSelectedResourceId(value);
                    const docsToSearch = documentSearchQuery ? searchResults : availableDocuments;
                    const selectedDoc = docsToSearch.find(
                      (doc) => doc.resource_id === value
                    );
                    if (selectedDoc) {
                      setSelectedResourceType(selectedDoc.file_type);
                    }
                  }}>
                    <SelectTrigger id="translation-doc-select">
                      <SelectValue placeholder="Select a document to translate..." />
                    </SelectTrigger>
                    <SelectContent className="max-h-[300px] overflow-y-auto">
                      {documentsLoading ? (
                        <SelectItem value="loading" disabled>
                          Loading documents...
                        </SelectItem>
                      ) : documentSearchQuery && searchResults.length === 0 && !isSearching ? (
                        <SelectItem value="none" disabled>
                          No documents found matching "{documentSearchQuery}"
                        </SelectItem>
                      ) : documentSearchQuery ? (
                        /* Show search results */
                        <>
                          <div className="px-2 py-1.5 text-xs font-semibold text-gray-500 bg-gray-50">
                            Search Results ({searchResults.length})
                          </div>
                          {searchResults.map((doc) => {
                            const docId = doc.resource_id;
                            const docName = doc.file_name || docId;
                            const docType = doc.file_type;
                            const isActive = doc.is_active || (doc.session_ids && doc.session_ids.length > 0);

                            return (
                              <SelectItem key={docId} value={docId}>
                                <div className="flex items-center gap-2">
                                  {isActive && (
                                    <CheckCircle2 className="w-3 h-3 text-green-600 flex-shrink-0" title="Active in current session" />
                                  )}
                                  <span>{docName}</span>
                                  <span className="text-xs text-gray-500">({docType})</span>
                                </div>
                              </SelectItem>
                            );
                          })}
                        </>
                      ) : availableDocuments.length === 0 ? (
                        <SelectItem value="none" disabled>
                          No documents available. Add documents first.
                        </SelectItem>
                      ) : (
                        <>
                          {/* Active documents section */}
                          {availableDocuments.filter(doc => doc.isActive).length > 0 && (
                            <>
                              <div className="px-2 py-1.5 text-xs font-semibold text-gray-500 bg-gray-50">
                                Session documents
                              </div>
                              {availableDocuments.filter(doc => doc.isActive).map((doc) => {
                                const docId = doc.resource_id;
                                const docName = doc.file_name || docId;
                                const docType = doc.file_type;

                                return (
                                  <SelectItem key={docId} value={docId}>
                                    <div className="flex items-center gap-2">
                                      <span className="text-blue-500" title="Active in current session">
                                        <CheckCircle2 className="w-3 h-3 text-green-600 flex-shrink-0" />
                                      </span>
                                      <span>{docName}</span>
                                      <span className="text-xs text-gray-500">({docType})</span>
                                    </div>
                                  </SelectItem>
                                );
                              })}
                            </>
                          )}

                          {/* Other documents section */}
                          {availableDocuments.filter(doc => !doc.isActive).length > 0 && (
                            <>
                              {availableDocuments.filter(doc => doc.isActive).length > 0 && (
                                <div className="px-2 py-1.5 text-xs font-semibold text-gray-500 bg-gray-50">
                                  Other Documents
                                </div>
                              )}
                              {availableDocuments.filter(doc => !doc.isActive).map((doc) => {
                                const docId = doc.resource_id;
                                const docName = doc.file_name || docId;
                                const docType = doc.file_type;

                                return (
                                  <SelectItem key={docId} value={docId}>
                                    <div className="flex items-center gap-2">
                                      <span>{docName}</span>
                                      <span className="text-xs text-gray-500">({docType})</span>
                                    </div>
                                  </SelectItem>
                                );
                              })}
                            </>
                          )}
                        </>
                      )}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500 mt-1">
                    {availableDocuments.length === 0
                      ? 'Add patents, publications, or uploads from the Documents page first'
                      : 'Select a document from your onboarded collection.'}
                  </p>
                </div>
              )}

              {/* Translation Language Selection */}
              {isTranslationTask() && translationInputMode && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="source-language" className="text-sm font-medium mb-2 block">
                      Source Language
                    </Label>
                    <Select value={sourceLanguage} onValueChange={setSourceLanguage}>
                      <SelectTrigger id="source-language">
                        <SelectValue placeholder="Select source language" />
                      </SelectTrigger>
                      <SelectContent>
                        {LANGUAGE_OPTIONS.map(lang => (
                          <SelectItem key={lang.value} value={lang.value}>
                            {lang.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="target-language" className="text-sm font-medium mb-2 block">
                      Target Language
                    </Label>
                    <Select value={targetLanguage} onValueChange={setTargetLanguage}>
                      <SelectTrigger id="target-language">
                        <SelectValue placeholder="Select target language" />
                      </SelectTrigger>
                      <SelectContent>
                        {LANGUAGE_OPTIONS.filter(l => l.value !== 'auto').map(lang => (
                          <SelectItem key={lang.value} value={lang.value}>
                            {lang.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              {/* File Upload for Document Translation */}
              {task.id === 'translate_document' && translationInputMode === 'upload' && (
                <div className="space-y-4">
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label htmlFor="translate-file" className="text-sm font-medium">
                        Upload Document to Translate
                      </Label>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setTranslationInputMode(null);
                          setUploadedFile(null);
                          const fileInput = document.getElementById('translate-file') as HTMLInputElement;
                          if (fileInput) fileInput.value = '';
                        }}
                        className="h-6 text-xs text-gray-500 hover:text-gray-700"
                      >
                        Change Input Method
                      </Button>
                    </div>

                    {/* Drag and Drop Area */}
                    <div
                      onDragOver={(e) => {
                        e.preventDefault();
                        setIsDragOver(true);
                      }}
                      onDragLeave={(e) => {
                        e.preventDefault();
                        setIsDragOver(false);
                      }}
                      onDrop={(e) => {
                        e.preventDefault();
                        setIsDragOver(false);
                        const file = e.dataTransfer.files?.[0];
                        if (file) {
                          setUploadedFile(file);
                          setError('');
                        }
                      }}
                      className={`relative border-2 border-dashed rounded-lg p-6 text-center transition-all duration-200 ${
                        isDragOver
                          ? 'border-blue-400 bg-blue-50'
                          : 'border-gray-300 bg-gray-50 hover:border-gray-400'
                      } ${isProcessing ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <input
                        id="translate-file"
                        type="file"
                        accept=".pdf,.docx,.txt,.doc"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            setUploadedFile(file);
                            setError('');
                          }
                        }}
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                        disabled={isProcessing}
                      />

                      <div className="space-y-3">
                        <div className="mx-auto w-12 h-12 rounded-full flex items-center justify-center bg-gray-200">
                          <Upload className="w-6 h-6 text-gray-600" />
                        </div>

                        <div>
                          <p className="text-sm font-medium text-gray-700">
                            {isDragOver ? 'Drop document here' : 'Drag and drop your document here'}
                          </p>
                          <p className="text-xs mt-1 text-gray-500">
                            or{' '}
                            <label
                              htmlFor="translate-file"
                              className="text-blue-500 hover:text-blue-600 cursor-pointer font-medium"
                            >
                              browse to upload
                            </label>
                          </p>
                        </div>

                        <div className="text-xs text-gray-400">
                          Supports PDF, DOCX, and TXT files
                        </div>
                      </div>
                    </div>

                    {/* Selected File Display */}
                    {uploadedFile && (
                      <div className="mt-3 flex items-center justify-between p-3 rounded-lg border bg-white border-gray-200">
                        <div className="flex items-center space-x-3 flex-1 min-w-0">
                          <FileText className="flex-shrink-0 w-5 h-5 text-gray-500" />
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium truncate text-gray-900">{uploadedFile.name}</p>
                            <p className="text-xs text-gray-500">
                              {(uploadedFile.size / 1024).toFixed(2)} KB
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() => {
                            setUploadedFile(null);
                            const fileInput = document.getElementById('translate-file') as HTMLInputElement;
                            if (fileInput) fileInput.value = '';
                          }}
                          disabled={isProcessing}
                          className="flex-shrink-0 ml-3 p-1 text-gray-400 hover:text-red-500 disabled:opacity-50"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Output Options (only shown when file is uploaded) */}
                  {uploadedFile && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="output-filename" className="text-sm font-medium mb-2 block">
                          Output Filename (Optional)
                        </Label>
                        <Input
                          id="output-filename"
                          value={outputFilename}
                          onChange={(e) => setOutputFilename(e.target.value)}
                          placeholder="e.g., translated_document.txt"
                          disabled={isProcessing}
                        />
                      </div>
                      <div>
                        <Label htmlFor="output-format" className="text-sm font-medium mb-2 block">
                          Output Format
                        </Label>
                        <Select value={outputFormat} onValueChange={setOutputFormat}>
                          <SelectTrigger id="output-format">
                            <SelectValue placeholder="Select format" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="txt">Text (.txt)</SelectItem>
                            <SelectItem value="docx">Word (.docx)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Text Input for Document Translation */}
              {task.id === 'translate_document' && translationInputMode === 'text' && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between mb-2">
                    <Label htmlFor="translation-text" className="text-sm font-medium">
                      Text to Translate
                    </Label>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setTranslationInputMode(null);
                        setTextInput('');
                      }}
                      className="h-6 text-xs text-gray-500 hover:text-gray-700"
                    >
                      Change Input Method
                    </Button>
                  </div>
                  <Textarea
                    id="translation-text"
                    value={textInput}
                    onChange={(e) => setTextInput(e.target.value)}
                    placeholder="Enter text to translate..."
                    className="min-h-[200px]"
                    disabled={isProcessing}
                  />
                </div>
              )}

              {/* Antecedent Basis Check Input Mode Selection */}
              {task.id === 'antecedent_basis_check' && !antecedentInputMode && (
                <div className="space-y-3">
                  <Label className="text-sm font-medium">Choose Input Method</Label>
                  <div className="grid grid-cols-1 gap-3">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setAntecedentInputMode('onboarded')}
                      className="h-auto py-4 flex flex-col items-center gap-2 hover:bg-blue-50 hover:border-blue-500"
                    >
                      <FileText className="w-6 h-6 text-blue-600" />
                      <div className="text-center">
                        <div className="font-medium">Select an Onboarded Document</div>
                        <div className="text-xs text-gray-500 mt-1">Choose from patents, publications, or uploads</div>
                      </div>
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        if (!isPaid) {
                          addNotification({
                            type: 'warning',
                            title: 'Pro or Premium Required',
                            message: 'File uploads are available for Pro and Premium subscribers. Please upgrade to access this feature.',
                            duration: 6000,
                          });
                          return;
                        }
                        setAntecedentInputMode('upload');
                      }}
                      disabled={!isPaid}
                      className={`h-auto py-4 flex flex-col items-center gap-2 ${
                        !isPaid
                          ? 'opacity-60 cursor-not-allowed'
                          : 'hover:bg-blue-50 hover:border-blue-500'
                      }`}
                    >
                      <Upload className={`w-6 h-6 ${!isPaid ? 'text-gray-400' : 'text-blue-600'}`} />
                      <div className="text-center">
                        <div className="flex items-center gap-2 justify-center">
                          <span className={`font-medium ${!isPaid ? 'text-gray-400' : ''}`}>Upload a Document</span>
                          {!isPaid && (
                            <Badge variant="outline" className="bg-purple-50 text-purple-800 border-purple-200 text-xs">
                              <Crown className="w-3 h-3 mr-1 inline" />
                              Pro/Premium
                            </Badge>
                          )}
                        </div>
                        <div className={`text-xs mt-1 ${!isPaid ? 'text-gray-400' : 'text-gray-500'}`}>Upload PDF, DOCX, TXT, or DOC file</div>
                      </div>
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setAntecedentInputMode('raw_text')}
                      className="h-auto py-4 flex flex-col items-center gap-2 hover:bg-blue-50 hover:border-blue-500"
                    >
                      <MessageSquarePlus className="w-6 h-6 text-blue-600" />
                      <div className="text-center">
                        <div className="font-medium">Enter Raw Claims Text</div>
                        <div className="text-xs text-gray-500 mt-1">Type or paste patent claims directly</div>
                      </div>
                    </Button>
                  </div>
                </div>
              )}

              {/* Document Selection for Antecedent Basis Check (Onboarded Mode) */}
              {task.id === 'antecedent_basis_check' && antecedentInputMode === 'onboarded' && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between mb-2">
                    <Label htmlFor="antecedent-doc-select" className="text-sm font-medium">
                      Select Document to Analyze
                    </Label>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setAntecedentInputMode(null);
                        setSelectedResourceId('');
                        setSelectedResourceType('');
                        setDocumentSearchQuery('');
                        setSearchResults([]);
                      }}
                      className="h-6 text-xs text-gray-500 hover:text-gray-700"
                    >
                      Change Input Method
                    </Button>
                  </div>

                  {/* Search Input */}
                  <div className="space-y-2">
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                        <Input
                          type="text"
                          placeholder="Search documents by name, ID, or title... (press Enter)"
                          value={documentSearchQuery}
                          onChange={(e) => setDocumentSearchQuery(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                            }
                          }}
                          className="pl-10 pr-10"
                          disabled={isSearching}
                        />
                        {documentSearchQuery && !isSearching && (
                          <button
                            onClick={() => {
                              setDocumentSearchQuery('');
                              setSearchResults([]);
                            }}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 pointer-events-auto"
                            type="button"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          if (documentSearchQuery.trim().length >= 2) {
                            setDocumentSearchQuery(documentSearchQuery + ' ');
                            setTimeout(() => setDocumentSearchQuery(documentSearchQuery.trim()), 0);
                          }
                        }}
                        disabled={isSearching || documentSearchQuery.trim().length < 2}
                        className="px-4"
                      >
                        {isSearching ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Search className="w-4 h-4" />
                        )}
                      </Button>
                    </div>

                    {/* Search Results Info */}
                    {documentSearchQuery.trim().length >= 2 && !isSearching && (
                      <div className="text-xs text-gray-600">
                        {searchResults.length > 0 ? (
                          <span className="text-green-700">
                            ✓ Found {searchResults.length} match{searchResults.length !== 1 ? 'es' : ''}
                          </span>
                        ) : (
                          <span className="text-amber-700">No documents found matching "{documentSearchQuery}"</span>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Document Selector */}
                  <Select value={selectedResourceId} onValueChange={(value) => {
                    setSelectedResourceId(value);
                    const docsToSearch = documentSearchQuery ? searchResults : availableDocuments;
                    const selectedDoc = docsToSearch.find(
                      (doc) => doc.resource_id === value
                    );
                    if (selectedDoc) {
                      setSelectedResourceType(selectedDoc.file_type);
                    }
                  }}>
                    <SelectTrigger id="antecedent-doc-select" disabled={documentsLoading || isProcessing}>
                      <SelectValue placeholder={
                        documentsLoading ? 'Loading documents...' :
                        availableDocuments.length === 0 ? 'No documents available' :
                        'Select a document'
                      } />
                    </SelectTrigger>
                    <SelectContent>
                      <ScrollArea className="max-h-[300px]">
                        {(documentSearchQuery ? searchResults : availableDocuments).map((doc) => (
                          <SelectItem
                            key={doc.resource_id}
                            value={doc.resource_id}
                            className="flex items-center gap-2"
                          >
                            <div className="flex items-center gap-2 w-full">
                              <FileText className="w-4 h-4 flex-shrink-0" />
                              <div className="flex-1 min-w-0">
                                <div className="font-medium text-sm truncate">
                                  {doc.file_name || doc.resource_id}
                                </div>
                                <div className="text-xs text-gray-500 flex items-center gap-2">
                                  <Badge variant="outline" className="text-[10px] px-1 py-0">
                                    {doc.file_type}
                                  </Badge>
                                  {doc.isActive && (
                                    <Badge variant="secondary" className="text-[10px] px-1 py-0 bg-green-100 text-green-800">
                                      Active
                                    </Badge>
                                  )}
                                </div>
                              </div>
                            </div>
                          </SelectItem>
                        ))}
                      </ScrollArea>
                    </SelectContent>
                  </Select>

                  {selectedResourceId && (
                    <div className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <CheckCircle2 className="w-4 h-4 text-blue-600 flex-shrink-0" />
                      <div className="text-sm text-blue-900">
                        <span className="font-medium">Selected: </span>
                        {availableDocuments.find(d => d.resource_id === selectedResourceId)?.file_name || selectedResourceId}
                        <span className="text-xs text-blue-700 ml-2">
                          ({selectedResourceType})
                        </span>
                      </div>
                    </div>
                  )}

                  <p className="text-xs text-gray-500">
                    Claims will be extracted from the selected document and analyzed for antecedent basis issues
                  </p>
                </div>
              )}

              {/* Raw Text Input for Antecedent Basis Check */}
              {task.id === 'antecedent_basis_check' && antecedentInputMode === 'raw_text' && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between mb-2">
                    <Label htmlFor="antecedent-claims-text" className="text-sm font-medium">
                      Claims Text
                    </Label>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setAntecedentInputMode(null);
                        setTextInput('');
                      }}
                      className="h-6 text-xs text-gray-500 hover:text-gray-700"
                    >
                      Change Input Method
                    </Button>
                  </div>
                  <Textarea
                    id="antecedent-claims-text"
                    value={textInput}
                    onChange={(e) => setTextInput(e.target.value)}
                    placeholder="Enter patent claims text (e.g., '1. A system comprising...')..."
                    className="min-h-[200px]"
                    disabled={isProcessing}
                  />
                  <p className="text-xs text-gray-500">
                    Enter claims with claim numbers (e.g., "1. A device...", "2. The device of claim 1...")
                  </p>
                </div>
              )}

              {/* File Upload for Antecedent Basis Check */}
              {task.id === 'antecedent_basis_check' && antecedentInputMode === 'upload' && (
                <div className="space-y-4">
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label htmlFor="antecedent-file" className="text-sm font-medium">
                        Upload Document with Claims
                      </Label>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setAntecedentInputMode(null);
                          setUploadedFile(null);
                          const fileInput = document.getElementById('antecedent-file') as HTMLInputElement;
                          if (fileInput) fileInput.value = '';
                        }}
                        className="h-6 text-xs text-gray-500 hover:text-gray-700"
                      >
                        Change Input Method
                      </Button>
                    </div>

                    {/* Drag and Drop Area */}
                    <div
                      onDragOver={(e) => {
                        e.preventDefault();
                        setIsDragOver(true);
                      }}
                      onDragLeave={(e) => {
                        e.preventDefault();
                        setIsDragOver(false);
                      }}
                      onDrop={(e) => {
                        e.preventDefault();
                        setIsDragOver(false);
                        const file = e.dataTransfer.files?.[0];
                        if (file) {
                          setUploadedFile(file);
                          setError('');
                        }
                      }}
                      className={`relative border-2 border-dashed rounded-lg p-6 text-center transition-all duration-200 ${
                        isDragOver
                          ? 'border-blue-400 bg-blue-50'
                          : 'border-gray-300 bg-gray-50 hover:border-gray-400'
                      } ${isProcessing ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <input
                        id="antecedent-file"
                        type="file"
                        accept=".pdf,.docx,.txt,.doc"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            setUploadedFile(file);
                            setError('');
                          }
                        }}
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                        disabled={isProcessing}
                      />

                      <div className="space-y-3">
                        <div className="mx-auto w-12 h-12 rounded-full flex items-center justify-center bg-gray-200">
                          <Upload className="w-6 h-6 text-gray-600" />
                        </div>

                        <div>
                          <p className="text-sm font-medium text-gray-700">
                            {isDragOver ? 'Drop document here' : 'Drag and drop your document here'}
                          </p>
                          <p className="text-xs mt-1 text-gray-500">
                            or click to browse
                          </p>
                        </div>

                        <p className="text-xs text-gray-400">
                          Supports PDF, DOCX, TXT, DOC
                        </p>

                        {uploadedFile && (
                          <div className="mt-3 p-3 bg-white border rounded flex items-center gap-2">
                            <FileText className="w-4 h-4 text-blue-600" />
                            <span className="text-sm font-medium">{uploadedFile.name}</span>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                setUploadedFile(null);
                                const fileInput = document.getElementById('antecedent-file') as HTMLInputElement;
                                if (fileInput) fileInput.value = '';
                              }}
                              className="ml-auto h-6 w-6 p-0"
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Technical Outline - Input Mode Selection */}
              {task.id === 'technical_outline' && !technicalOutlineInputMode && (
                <div className="space-y-3">
                  <Label className="text-sm font-medium">Choose Input Method</Label>
                  <div className="grid grid-cols-1 gap-3">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        if (!isPaid) {
                          addNotification({
                            type: 'warning',
                            title: 'Pro or Premium Required',
                            message: 'File uploads are available for Pro and Premium subscribers. Please upgrade to access this feature.',
                            duration: 6000,
                          });
                          return;
                        }
                        setTechnicalOutlineInputMode('upload');
                      }}
                      disabled={!isPaid}
                      className={`h-auto py-4 flex flex-col items-center gap-2 ${
                        !isPaid
                          ? 'opacity-60 cursor-not-allowed'
                          : 'hover:bg-blue-50 hover:border-blue-500'
                      }`}
                    >
                      <Upload className={`w-6 h-6 ${!isPaid ? 'text-gray-400' : 'text-blue-600'}`} />
                      <div className="text-center">
                        <div className="flex items-center gap-2 justify-center">
                          <span className={`font-medium ${!isPaid ? 'text-gray-400' : ''}`}>Upload Disclosure Document</span>
                          {!isPaid && (
                            <Badge variant="outline" className="bg-purple-50 text-purple-800 border-purple-200 text-xs">
                              <Crown className="w-3 h-3 mr-1 inline" />
                              Pro/Premium
                            </Badge>
                          )}
                        </div>
                        <div className={`text-xs mt-1 ${!isPaid ? 'text-gray-400' : 'text-gray-500'}`}>Upload PDF, DOCX, TXT, or DOC file</div>
                      </div>
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setTechnicalOutlineInputMode('raw_text')}
                      className="h-auto py-4 flex flex-col items-center gap-2 hover:bg-blue-50 hover:border-blue-500"
                    >
                      <MessageSquarePlus className="w-6 h-6 text-blue-600" />
                      <div className="text-center">
                        <div className="font-medium">Enter Disclosure Text</div>
                        <div className="text-xs text-gray-500 mt-1">Type or paste invention disclosure directly</div>
                      </div>
                    </Button>
                  </div>
                </div>
              )}

              {/* Technical Outline - Raw Text Input */}
              {task.id === 'technical_outline' && technicalOutlineInputMode === 'raw_text' && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between mb-2">
                    <Label htmlFor="technical-outline-text" className="text-sm font-medium">
                      Invention Disclosure Text
                    </Label>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setTechnicalOutlineInputMode(null);
                        setTextInput('');
                      }}
                      className="h-6 text-xs text-gray-500 hover:text-gray-700"
                    >
                      Change Input Method
                    </Button>
                  </div>
                  <Textarea
                    id="technical-outline-text"
                    placeholder="Enter your invention disclosure text here. Include details about the technical field, problems addressed, innovative features, components, and how the invention works..."
                    value={textInput}
                    onChange={(e) => {
                      setTextInput(e.target.value);
                      setError('');
                    }}
                    className="min-h-[300px] font-mono text-sm"
                    disabled={isProcessing}
                  />
                  <p className="text-xs text-gray-500">
                    The more detailed your disclosure, the more comprehensive the outline will be.
                  </p>
                </div>
              )}

              {/* Technical Outline - Upload Input */}
              {task.id === 'technical_outline' && technicalOutlineInputMode === 'upload' && (
                <div className="space-y-4">
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label htmlFor="technical-outline-file" className="text-sm font-medium">
                        Upload Disclosure Document
                      </Label>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setTechnicalOutlineInputMode(null);
                          setUploadedFile(null);
                          const fileInput = document.getElementById('technical-outline-file') as HTMLInputElement;
                          if (fileInput) fileInput.value = '';
                        }}
                        className="h-6 text-xs text-gray-500 hover:text-gray-700"
                      >
                        Change Input Method
                      </Button>
                    </div>

                    {/* Drag and Drop Area */}
                    <div
                      onDragOver={(e) => {
                        e.preventDefault();
                        setIsDragOver(true);
                      }}
                      onDragLeave={(e) => {
                        e.preventDefault();
                        setIsDragOver(false);
                      }}
                      onDrop={(e) => {
                        e.preventDefault();
                        setIsDragOver(false);
                        const file = e.dataTransfer.files?.[0];
                        if (file) {
                          setUploadedFile(file);
                          setError('');
                        }
                      }}
                      className={`relative border-2 border-dashed rounded-lg p-6 text-center transition-all duration-200 ${
                        isDragOver
                          ? 'border-blue-400 bg-blue-50'
                          : 'border-gray-300 bg-gray-50 hover:border-gray-400'
                      } ${isProcessing ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <input
                        id="technical-outline-file"
                        type="file"
                        accept=".pdf,.docx,.txt,.doc"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            setUploadedFile(file);
                            setError('');
                          }
                        }}
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                        disabled={isProcessing}
                      />
                      <div className="flex flex-col items-center gap-3 pointer-events-none">
                        <div className="p-3 bg-white rounded-full border border-gray-200">
                          <Upload className="w-6 h-6 text-blue-600" />
                        </div>
                        <div>
                          <p className="text-sm font-medium">
                            Drop your file here or click to browse
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            Drag and drop your disclosure document
                          </p>
                        </div>
                        <p className="text-xs text-gray-400">
                          Supports PDF, DOCX, TXT, DOC
                        </p>

                        {uploadedFile && (
                          <div className="mt-3 p-3 bg-white border rounded flex items-center gap-2">
                            <FileText className="w-4 h-4 text-blue-600" />
                            <span className="text-sm font-medium">{uploadedFile.name}</span>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                setUploadedFile(null);
                                const fileInput = document.getElementById('technical-outline-file') as HTMLInputElement;
                                if (fileInput) fileInput.value = '';
                              }}
                              className="ml-auto h-6 w-6 p-0"
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Parts & Process Outline - Input Mode Selection */}
              {task.id === 'parts_process_outline' && !partsProcessOutlineInputMode && (
                <div className="space-y-3">
                  <Label className="text-sm font-medium">Choose Input Method</Label>
                  <div className="grid grid-cols-1 gap-3">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        if (!isPaid) {
                          addNotification({
                            type: 'warning',
                            title: 'Pro or Premium Required',
                            message: 'File uploads are available for Pro and Premium subscribers. Please upgrade to access this feature.',
                            duration: 6000,
                          });
                          return;
                        }
                        setPartsProcessOutlineInputMode('upload');
                      }}
                      disabled={!isPaid}
                      className={`h-auto py-4 flex flex-col items-center gap-2 ${
                        !isPaid
                          ? 'opacity-60 cursor-not-allowed'
                          : 'hover:bg-blue-50 hover:border-blue-500'
                      }`}
                    >
                      <Upload className={`w-6 h-6 ${!isPaid ? 'text-gray-400' : 'text-blue-600'}`} />
                      <div className="text-center">
                        <div className="flex items-center gap-2 justify-center">
                          <span className={`font-medium ${!isPaid ? 'text-gray-400' : ''}`}>Upload Disclosure Document</span>
                          {!isPaid && (
                            <Badge variant="outline" className="bg-purple-50 text-purple-800 border-purple-200 text-xs">
                              <Crown className="w-3 h-3 mr-1 inline" />
                              Pro/Premium
                            </Badge>
                          )}
                        </div>
                        <div className={`text-xs mt-1 ${!isPaid ? 'text-gray-400' : 'text-gray-500'}`}>Upload PDF, DOCX, TXT, or DOC file</div>
                      </div>
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setPartsProcessOutlineInputMode('raw_text')}
                      className="h-auto py-4 flex flex-col items-center gap-2 hover:bg-blue-50 hover:border-blue-500"
                    >
                      <MessageSquarePlus className="w-6 h-6 text-blue-600" />
                      <div className="text-center">
                        <div className="font-medium">Enter Disclosure Text</div>
                        <div className="text-xs text-gray-500 mt-1">Type or paste invention disclosure directly</div>
                      </div>
                    </Button>
                  </div>
                </div>
              )}

              {/* Parts & Process Outline - Raw Text Input */}
              {task.id === 'parts_process_outline' && partsProcessOutlineInputMode === 'raw_text' && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between mb-2">
                    <Label htmlFor="parts-process-outline-text" className="text-sm font-medium">
                      Invention Disclosure Text
                    </Label>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setPartsProcessOutlineInputMode(null);
                        setTextInput('');
                      }}
                      className="h-6 text-xs text-gray-500 hover:text-gray-700"
                    >
                      Change Input Method
                    </Button>
                  </div>
                  <Textarea
                    id="parts-process-outline-text"
                    placeholder="Enter your invention disclosure text here. Include detailed descriptions of components, parts, subparts, their relationships, process steps, and how the system operates..."
                    value={textInput}
                    onChange={(e) => {
                      setTextInput(e.target.value);
                      setError('');
                    }}
                    className="min-h-[300px] font-mono text-sm"
                    disabled={isProcessing}
                  />
                  <p className="text-xs text-gray-500">
                    Focus on specific parts, their technical details, and relationships for best results.
                  </p>
                </div>
              )}

              {/* Parts & Process Outline - Upload Input */}
              {task.id === 'parts_process_outline' && partsProcessOutlineInputMode === 'upload' && (
                <div className="space-y-4">
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label htmlFor="parts-process-outline-file" className="text-sm font-medium">
                        Upload Disclosure Document
                      </Label>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setPartsProcessOutlineInputMode(null);
                          setUploadedFile(null);
                          const fileInput = document.getElementById('parts-process-outline-file') as HTMLInputElement;
                          if (fileInput) fileInput.value = '';
                        }}
                        className="h-6 text-xs text-gray-500 hover:text-gray-700"
                      >
                        Change Input Method
                      </Button>
                    </div>

                    {/* Drag and Drop Area */}
                    <div
                      onDragOver={(e) => {
                        e.preventDefault();
                        setIsDragOver(true);
                      }}
                      onDragLeave={(e) => {
                        e.preventDefault();
                        setIsDragOver(false);
                      }}
                      onDrop={(e) => {
                        e.preventDefault();
                        setIsDragOver(false);
                        const file = e.dataTransfer.files?.[0];
                        if (file) {
                          setUploadedFile(file);
                          setError('');
                        }
                      }}
                      className={`relative border-2 border-dashed rounded-lg p-6 text-center transition-all duration-200 ${
                        isDragOver
                          ? 'border-blue-400 bg-blue-50'
                          : 'border-gray-300 bg-gray-50 hover:border-gray-400'
                      } ${isProcessing ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <input
                        id="parts-process-outline-file"
                        type="file"
                        accept=".pdf,.docx,.txt,.doc"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            setUploadedFile(file);
                            setError('');
                          }
                        }}
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                        disabled={isProcessing}
                      />
                      <div className="flex flex-col items-center gap-3 pointer-events-none">
                        <div className="p-3 bg-white rounded-full border border-gray-200">
                          <Upload className="w-6 h-6 text-blue-600" />
                        </div>
                        <div>
                          <p className="text-sm font-medium">
                            Drop your file here or click to browse
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            Drag and drop your disclosure document
                          </p>
                        </div>
                        <p className="text-xs text-gray-400">
                          Supports PDF, DOCX, TXT, DOC
                        </p>

                        {uploadedFile && (
                          <div className="mt-3 p-3 bg-white border rounded flex items-center gap-2">
                            <FileText className="w-4 h-4 text-blue-600" />
                            <span className="text-sm font-medium">{uploadedFile.name}</span>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                setUploadedFile(null);
                                const fileInput = document.getElementById('parts-process-outline-file') as HTMLInputElement;
                                if (fileInput) fileInput.value = '';
                              }}
                              className="ml-auto h-6 w-6 p-0"
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Prior Art Analysis Form */}
              {task.id === 'prior_art_analysis' && (
                <div className="space-y-4">
                  <div>
                    <Label className="text-sm font-medium mb-1 block">Technology Description</Label>
                    <p className="text-xs text-gray-500 mb-2">
                      Describe the technology or paste a patent number below (at least one required)
                    </p>
                    <textarea
                      className="w-full border border-gray-300 rounded-md p-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                      rows={5}
                      placeholder="Describe the target technology in detail..."
                      value={priorArtTechDescription}
                      onChange={(e) => setPriorArtTechDescription(e.target.value)}
                      disabled={isProcessing}
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium mb-1 block">Target Patent Number <span className="font-normal text-gray-400">(optional, used instead of description)</span></Label>
                    <Input
                      placeholder="e.g. US12345678"
                      value={priorArtResourceId}
                      onChange={(e) => setPriorArtResourceId(e.target.value)}
                      disabled={isProcessing}
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium mb-1 block">Prior Art Publication Numbers</Label>
                    <p className="text-xs text-gray-500 mb-2">
                      One per line or comma-separated (e.g. US9876543, CN121147185A)
                    </p>
                    <textarea
                      className="w-full border border-gray-300 rounded-md p-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                      rows={6}
                      placeholder={"US9876543\nUS10123456\nEP1234567A1"}
                      value={priorArtPubNumbers}
                      onChange={(e) => setPriorArtPubNumbers(e.target.value)}
                      disabled={isProcessing}
                    />
                  </div>
                </div>
              )}

              {/* Patent Drawing Form */}
              {task.id === 'patent_drawing' && (
                <div className="space-y-4">
                  <p className="text-xs text-gray-500">
                    Provide an image, a description, or both — at least one is required.
                  </p>
                  <div>
                    <Label className="text-sm font-medium mb-1 block">
                      Upload Image <span className="font-normal text-gray-400">(optional)</span>
                    </Label>
                    <p className="text-xs text-gray-500 mb-2">
                      Upload a photo or sketch of your invention (PNG, JPEG, WEBP, or GIF)
                    </p>
                    <input
                      id="patent-drawing-file"
                      type="file"
                      accept="image/png,image/jpeg,image/webp,image/gif"
                      className="hidden"
                      onChange={(e) => {
                        const f = e.target.files?.[0] || null;
                        setPatentDrawingFile(f);
                      }}
                      disabled={isProcessing}
                    />
                    {!patentDrawingFile ? (
                      <div
                        className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-blue-400 transition-colors"
                        onClick={() => document.getElementById('patent-drawing-file')?.click()}
                      >
                        <Upload className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                        <p className="text-sm text-gray-500">Click to upload an image</p>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                        <FileText className="w-4 h-4 text-blue-500 flex-shrink-0" />
                        <span className="text-sm text-gray-700 truncate flex-1">{patentDrawingFile.name}</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setPatentDrawingFile(null);
                            const fi = document.getElementById('patent-drawing-file') as HTMLInputElement;
                            if (fi) fi.value = '';
                          }}
                          className="ml-auto h-6 w-6 p-0"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                  <div>
                    <Label className="text-sm font-medium mb-1 block">
                      {patentDrawingFile ? 'Additional Instruction' : 'Describe the Drawing'}{' '}
                      <span className="font-normal text-gray-400">
                        {patentDrawingFile ? '(optional)' : '(required if no image)'}
                      </span>
                    </Label>
                    <Textarea
                      rows={3}
                      placeholder={
                        patentDrawingFile
                          ? 'e.g. Show cross-section view, emphasize component 3...'
                          : 'e.g. A handheld device with a cylindrical body, a trigger on the underside, and a nozzle extending from the front...'
                      }
                      value={patentDrawingInstruction}
                      onChange={(e) => setPatentDrawingInstruction(e.target.value)}
                      disabled={isProcessing}
                    />
                  </div>
                  {error && (
                    <p className="text-sm text-red-600">{error}</p>
                  )}
                </div>
              )}

              {/* Patent Document Selection */}
              {requiresPatentSelection() && !requiresDocumentSelection() && (
                <div>
                  <Label htmlFor="patent-number" className="text-sm font-medium mb-2 block">
                    Patent Number (Optional)
                  </Label>
                  <Input
                    id="patent-number"
                    value={patentNumber}
                    onChange={(e) => setPatentNumber(e.target.value)}
                    placeholder="e.g., US10123456, US20210123456"
                    disabled={isProcessing}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Optionally provide a patent number for enhanced context
                  </p>
                </div>
              )}

              {/* Application Number for Office Action/Form tasks */}
              {requiresApplicationNumber() && (
                <div>
                  <Label htmlFor="application-number" className="text-sm font-medium mb-2 block">
                    Application Number (Optional)
                  </Label>
                  <Input
                    id="application-number"
                    value={applicationNumber}
                    onChange={(e) => setApplicationNumber(e.target.value)}
                    placeholder="e.g., 16/123,456"
                    disabled={isProcessing}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Optionally provide an application number for context
                  </p>
                </div>
              )}

              {/* File Upload for LaTeX Conversion */}
              {task.id === 'latex_to_docx' && (
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="latex-file" className="text-sm font-medium mb-2 block">
                      LaTeX Document
                    </Label>

                    {/* Drag and Drop Area */}
                    <div
                      onDragOver={(e) => {
                        e.preventDefault();
                        setIsDragOver(true);
                      }}
                      onDragLeave={(e) => {
                        e.preventDefault();
                        setIsDragOver(false);
                      }}
                      onDrop={(e) => {
                        e.preventDefault();
                        setIsDragOver(false);
                        const file = e.dataTransfer.files?.[0];
                        if (file) {
                          setUploadedFile(file);
                          setError('');
                        }
                      }}
                      className={`relative border-2 border-dashed rounded-lg p-6 text-center transition-all duration-200 ${
                        isDragOver
                          ? 'border-blue-400 bg-blue-50'
                          : 'border-gray-300 bg-gray-50 hover:border-gray-400'
                      } ${isProcessing ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <input
                        id="latex-file"
                        type="file"
                        accept=".tex,.zip,.bib,.sty,.cls,.bst"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            setUploadedFile(file);
                            setError('');
                          }
                        }}
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                        disabled={isProcessing}
                      />

                      <div className="space-y-3">
                        <div className="mx-auto w-12 h-12 rounded-full flex items-center justify-center bg-gray-200">
                          <Upload className="w-6 h-6 text-gray-600" />
                        </div>

                        <div>
                          <p className="text-sm font-medium text-gray-700">
                            {isDragOver ? 'Drop LaTeX file here' : 'Drag and drop your LaTeX file here'}
                          </p>
                          <p className="text-xs mt-1 text-gray-500">
                            or{' '}
                            <label
                              htmlFor="latex-file"
                              className="text-blue-500 hover:text-blue-600 cursor-pointer font-medium"
                            >
                              browse to upload
                            </label>
                          </p>
                        </div>

                        <div className="text-xs text-gray-400">
                          Supports .tex, .zip, .bib, .sty, .cls, .bst files
                        </div>
                      </div>
                    </div>

                    {/* Selected File Display */}
                    {uploadedFile && (
                      <div className="mt-3 flex items-center justify-between p-3 rounded-lg border bg-white border-gray-200">
                        <div className="flex items-center space-x-3 flex-1 min-w-0">
                          <FileText className="flex-shrink-0 w-5 h-5 text-gray-500" />
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium truncate text-gray-900">{uploadedFile.name}</p>
                            <p className="text-xs text-gray-500">
                              {(uploadedFile.size / 1024).toFixed(2)} KB
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() => {
                            setUploadedFile(null);
                            setError('');
                          }}
                          className="flex-shrink-0 p-1 rounded-full transition-colors text-gray-400 hover:text-red-500 hover:bg-red-50"
                          disabled={isProcessing}
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    )}

                    <p className="text-xs text-gray-500 mt-2">
                      <span className="text-amber-600">Note: Complex equations may include LaTeX source for verification</span>
                    </p>
                  </div>

                  <div>
                    <Label htmlFor="output-filename" className="text-sm font-medium mb-2 block">
                      Output Filename (optional)
                    </Label>
                    <Input
                      id="output-filename"
                      value={outputFilename}
                      onChange={(e) => setOutputFilename(e.target.value)}
                      placeholder="e.g., my_document.docx"
                      disabled={isProcessing}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Leave empty to use default filename
                    </p>
                  </div>
                  {error && (
                    <p className="text-sm text-red-600">{error}</p>
                  )}
                </div>
              )}

              {/* OCR PDF Upload */}
              {task.id === 'ocr_pdf' && (
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="ocr-pdf-file" className="text-sm font-medium mb-2 block">
                      PDF Document
                    </Label>

                    {/* Drag and Drop Area */}
                    <div
                      onDragOver={(e) => {
                        e.preventDefault();
                        setIsDragOver(true);
                      }}
                      onDragLeave={(e) => {
                        e.preventDefault();
                        setIsDragOver(false);
                      }}
                      onDrop={(e) => {
                        e.preventDefault();
                        setIsDragOver(false);
                        const file = e.dataTransfer.files?.[0];
                        if (file) {
                          setUploadedFile(file);
                          setError('');
                        }
                      }}
                      className={`relative border-2 border-dashed rounded-lg p-6 text-center transition-all duration-200 ${
                        isDragOver
                          ? 'border-blue-400 bg-blue-50'
                          : 'border-gray-300 bg-gray-50 hover:border-gray-400'
                      } ${isProcessing ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <input
                        id="ocr-pdf-file"
                        type="file"
                        accept=".pdf"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            setUploadedFile(file);
                            setError('');
                          }
                        }}
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                        disabled={isProcessing}
                      />

                      <div className="space-y-3">
                        <div className="mx-auto w-12 h-12 rounded-full flex items-center justify-center bg-gray-200">
                          <Upload className="w-6 h-6 text-gray-600" />
                        </div>

                        <div>
                          <p className="text-sm font-medium text-gray-700">
                            {isDragOver ? 'Drop PDF file here' : 'Drag and drop your PDF file here'}
                          </p>
                          <p className="text-xs mt-1 text-gray-500">
                            or{' '}
                            <label
                              htmlFor="ocr-pdf-file"
                              className="text-blue-500 hover:text-blue-600 cursor-pointer font-medium"
                            >
                              browse to upload
                            </label>
                          </p>
                        </div>

                        <div className="text-xs text-gray-400">
                          Supports PDF files only
                        </div>
                      </div>
                    </div>

                    {/* Selected File Display */}
                    {uploadedFile && (
                      <div className="mt-3 flex items-center justify-between p-3 rounded-lg border bg-white border-gray-200">
                        <div className="flex items-center space-x-3 flex-1 min-w-0">
                          <FileText className="flex-shrink-0 w-5 h-5 text-gray-500" />
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium truncate text-gray-900">{uploadedFile.name}</p>
                            <p className="text-xs text-gray-500">
                              {(uploadedFile.size / 1024).toFixed(2)} KB
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() => {
                            setUploadedFile(null);
                            setError('');
                          }}
                          className="flex-shrink-0 p-1 rounded-full transition-colors text-gray-400 hover:text-red-500 hover:bg-red-50"
                          disabled={isProcessing}
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    )}

                    <p className="text-xs text-gray-500 mt-2">
                      <span className="text-amber-600">Note: OCR processing may take longer for scanned documents with many pages</span>
                    </p>
                  </div>

                  <div>
                    <Label htmlFor="output-format" className="text-sm font-medium mb-2 block">
                      Output Format
                    </Label>
                    <select
                      id="output-format"
                      value={outputFormat}
                      onChange={(e) => setOutputFormat(e.target.value as 'txt' | 'json' | 'docx')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                      disabled={isProcessing}
                    >
                      <option value="txt">Text (.txt) - Plain text output</option>
                      <option value="docx">Word (.docx) - Formatted document</option>
                      <option value="json">JSON (.json) - Includes positional data</option>
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      Choose Word for formatted document or JSON for detailed bounding box information
                    </p>
                  </div>

                  <div>
                    <Label htmlFor="ocr-output-filename" className="text-sm font-medium mb-2 block">
                      Output Filename (optional)
                    </Label>
                    <Input
                      id="ocr-output-filename"
                      value={outputFilename}
                      onChange={(e) => setOutputFilename(e.target.value)}
                      placeholder={`e.g., ocr_output.${outputFormat}`}
                      disabled={isProcessing}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Leave empty to use default filename
                    </p>
                  </div>
                  {error && (
                    <p className="text-sm text-red-600">{error}</p>
                  )}
                </div>
              )}

              {/* Draft Tasks Input Mode Selection */}
              {task.id.includes('draft_') && !draftInputMode && (
                <div className="space-y-3">
                  <Label className="text-sm font-medium">Choose Input Method</Label>
                  <div className="grid grid-cols-1 gap-3">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setDraftInputMode('onboarded')}
                      className="h-auto py-4 flex flex-col items-center gap-2 hover:bg-blue-50 hover:border-blue-500"
                    >
                      <FileText className="w-6 h-6 text-blue-600" />
                      <div className="text-center">
                        <div className="font-medium">Select an Onboarded Document</div>
                        <div className="text-xs text-gray-500 mt-1">Choose from patents, publications, or uploads</div>
                      </div>
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        if (!isPaid) {
                          addNotification({
                            type: 'warning',
                            title: 'Pro or Premium Required',
                            message: 'File uploads are available for Pro and Premium subscribers. Please upgrade to access this feature.',
                            duration: 6000,
                          });
                          return;
                        }
                        setDraftInputMode('upload');
                      }}
                      disabled={!isPaid}
                      className={`h-auto py-4 flex flex-col items-center gap-2 ${
                        !isPaid
                          ? 'opacity-60 cursor-not-allowed'
                          : 'hover:bg-blue-50 hover:border-blue-500'
                      }`}
                    >
                      <Upload className={`w-6 h-6 ${!isPaid ? 'text-gray-400' : 'text-blue-600'}`} />
                      <div className="text-center">
                        <div className="flex items-center gap-2 justify-center">
                          <span className={`font-medium ${!isPaid ? 'text-gray-400' : ''}`}>Upload a Document</span>
                          {!isPaid && (
                            <Badge variant="outline" className="bg-purple-50 text-purple-800 border-purple-200 text-xs">
                              <Crown className="w-3 h-3 mr-1 inline" />
                              Pro/Premium
                            </Badge>
                          )}
                        </div>
                        <div className={`text-xs mt-1 ${!isPaid ? 'text-gray-400' : 'text-gray-500'}`}>Upload PDF, DOCX, TXT, or DOC file</div>
                      </div>
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setDraftInputMode('raw_text')}
                      className="h-auto py-4 flex flex-col items-center gap-2 hover:bg-blue-50 hover:border-blue-500"
                    >
                      <MessageSquarePlus className="w-6 h-6 text-blue-600" />
                      <div className="text-center">
                        <div className="font-medium">Enter Disclosure Text</div>
                        <div className="text-xs text-gray-500 mt-1">Type or paste invention disclosure directly</div>
                      </div>
                    </Button>
                  </div>
                </div>
              )}

              {/* Document Selection for Draft Tasks (Onboarded Mode) */}
              {task.id.includes('draft_') && draftInputMode === 'onboarded' && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between mb-2">
                    <Label htmlFor="draft-doc-select" className="text-sm font-medium">
                      Select Reference Document
                    </Label>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setDraftInputMode(null);
                        setSelectedResourceId('');
                        setSelectedResourceType('');
                        setDocumentSearchQuery('');
                        setSearchResults([]);
                      }}
                      className="h-6 text-xs text-gray-500 hover:text-gray-700"
                    >
                      Change Input Method
                    </Button>
                  </div>

                  {/* Search Input */}
                  <div className="space-y-2">
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                        <Input
                          type="text"
                          placeholder="Search documents by name, ID, or title... (press Enter)"
                          value={documentSearchQuery}
                          onChange={(e) => setDocumentSearchQuery(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                            }
                          }}
                          className="pl-10 pr-10"
                          disabled={isSearching}
                        />
                        {documentSearchQuery && !isSearching && (
                          <button
                            onClick={() => {
                              setDocumentSearchQuery('');
                              setSearchResults([]);
                            }}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 pointer-events-auto"
                            type="button"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          if (documentSearchQuery.trim().length >= 2) {
                            setDocumentSearchQuery(documentSearchQuery + ' ');
                            setTimeout(() => setDocumentSearchQuery(documentSearchQuery.trim()), 0);
                          }
                        }}
                        disabled={isSearching || documentSearchQuery.trim().length < 2}
                        className="px-4"
                      >
                        {isSearching ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Search className="w-4 h-4" />
                        )}
                      </Button>
                    </div>

                    {/* Search Results Info */}
                    {documentSearchQuery.trim().length >= 2 && !isSearching && (
                      <div className="text-xs text-gray-600">
                        {searchResults.length > 0 ? (
                          <span className="text-green-700">
                            ✓ Found {searchResults.length} match{searchResults.length !== 1 ? 'es' : ''}
                          </span>
                        ) : (
                          <span className="text-amber-700">No documents found matching "{documentSearchQuery}"</span>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Document Selector */}
                  <Select value={selectedResourceId} onValueChange={(value) => {
                    setSelectedResourceId(value);
                    const docsToSearch = documentSearchQuery ? searchResults : availableDocuments;
                    const selectedDoc = docsToSearch.find(
                      (doc) => doc.resource_id === value
                    );
                    if (selectedDoc) {
                      setSelectedResourceType(selectedDoc.file_type);
                    }
                  }}>
                    <SelectTrigger id="draft-doc-select" disabled={documentsLoading || isProcessing}>
                      <SelectValue placeholder={
                        documentsLoading ? 'Loading documents...' :
                        availableDocuments.length === 0 ? 'No documents available' :
                        'Select a document'
                      } />
                    </SelectTrigger>
                    <SelectContent>
                      <ScrollArea className="max-h-[300px]">
                        {(documentSearchQuery ? searchResults : availableDocuments).map((doc) => (
                          <SelectItem
                            key={doc.resource_id}
                            value={doc.resource_id}
                            className="flex items-center gap-2"
                          >
                            <div className="flex items-center gap-2 w-full">
                              <FileText className="w-4 h-4 flex-shrink-0" />
                              <div className="flex-1 min-w-0">
                                <div className="font-medium text-sm truncate">
                                  {doc.file_name || doc.resource_id}
                                </div>
                                <div className="text-xs text-gray-500 flex items-center gap-2">
                                  <Badge variant="outline" className="text-[10px] px-1 py-0">
                                    {doc.file_type}
                                  </Badge>
                                  {doc.isActive && (
                                    <Badge variant="secondary" className="text-[10px] px-1 py-0 bg-green-100 text-green-800">
                                      Active
                                    </Badge>
                                  )}
                                </div>
                              </div>
                            </div>
                          </SelectItem>
                        ))}
                      </ScrollArea>
                    </SelectContent>
                  </Select>

                  {selectedResourceId && (
                    <div className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <CheckCircle2 className="w-4 h-4 text-blue-600 flex-shrink-0" />
                      <div className="text-sm text-blue-900">
                        <span className="font-medium">Selected: </span>
                        {availableDocuments.find(d => d.resource_id === selectedResourceId)?.file_name || selectedResourceId}
                        <span className="text-xs text-blue-700 ml-2">
                          ({selectedResourceType})
                        </span>
                      </div>
                    </div>
                  )}

                  <p className="text-xs text-gray-500">
                    This document will be used as a reference to inform the drafting process
                  </p>
                </div>
              )}

              {/* Raw Text Input for Draft Tasks */}
              {task.id.includes('draft_') && draftInputMode === 'raw_text' && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between mb-2">
                    <Label htmlFor="draft-disclosure-text" className="text-sm font-medium">
                      Invention Disclosure Text
                    </Label>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setDraftInputMode(null);
                        setDraftTextContent('');
                      }}
                      className="h-6 text-xs text-gray-500 hover:text-gray-700"
                    >
                      Change Input Method
                    </Button>
                  </div>
                  <Textarea
                    id="draft-disclosure-text"
                    value={draftTextContent}
                    onChange={(e) => setDraftTextContent(e.target.value)}
                    placeholder="Enter your invention disclosure or technical content here. Include details about the technical field, problems addressed, innovative features, components, and how the invention works..."
                    className="min-h-[300px] font-mono text-sm"
                    disabled={isProcessing}
                  />
                  <p className="text-xs text-gray-500">
                    The more detailed your disclosure, the better the draft output will be.
                  </p>
                </div>
              )}

              {/* File Upload for Draft Tasks */}
              {task.id.includes('draft_') && draftInputMode === 'upload' && (
                <div className="space-y-4">
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label htmlFor="draft-file" className="text-sm font-medium">
                        Upload Disclosure Document
                      </Label>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setDraftInputMode(null);
                          setUploadedFile(null);
                          const fileInput = document.getElementById('draft-file') as HTMLInputElement;
                          if (fileInput) fileInput.value = '';
                        }}
                        className="h-6 text-xs text-gray-500 hover:text-gray-700"
                      >
                        Change Input Method
                      </Button>
                    </div>

                    {/* Drag and Drop Area */}
                    <div
                      onDragOver={(e) => {
                        e.preventDefault();
                        setIsDragOver(true);
                      }}
                      onDragLeave={(e) => {
                        e.preventDefault();
                        setIsDragOver(false);
                      }}
                      onDrop={(e) => {
                        e.preventDefault();
                        setIsDragOver(false);
                        const file = e.dataTransfer.files?.[0];
                        if (file) {
                          setUploadedFile(file);
                          setError('');
                        }
                      }}
                      className={`relative border-2 border-dashed rounded-lg p-6 text-center transition-all duration-200 ${
                        isDragOver
                          ? 'border-blue-400 bg-blue-50'
                          : 'border-gray-300 bg-gray-50 hover:border-gray-400'
                      } ${isProcessing ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <input
                        id="draft-file"
                        type="file"
                        accept=".pdf,.docx,.txt,.doc"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            setUploadedFile(file);
                            setError('');
                          }
                        }}
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                        disabled={isProcessing}
                      />
                      <div className="flex flex-col items-center gap-3 pointer-events-none">
                        <div className="p-3 bg-white rounded-full border border-gray-200">
                          <Upload className="w-6 h-6 text-blue-600" />
                        </div>
                        <div>
                          <p className="text-sm font-medium">
                            Drop your file here or click to browse
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            Drag and drop your disclosure document
                          </p>
                        </div>
                        <p className="text-xs text-gray-400">
                          Supports PDF, DOCX, TXT, DOC
                        </p>

                        {uploadedFile && (
                          <div className="mt-3 p-3 bg-white border rounded flex items-center gap-2">
                            <FileText className="w-4 h-4 text-blue-600" />
                            <span className="text-sm font-medium">{uploadedFile.name}</span>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                setUploadedFile(null);
                                const fileInput = document.getElementById('draft-file') as HTMLInputElement;
                                if (fileInput) fileInput.value = '';
                              }}
                              className="ml-auto h-6 w-6 p-0"
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Draft Tasks Instructions (shown after input mode is selected) */}
              {task.id.includes('draft_') && draftInputMode && (
                <div className="space-y-3">
                  <Label htmlFor="draft-instructions" className="text-sm font-medium">
                    Drafting Instructions (Optional)
                  </Label>
                  <Textarea
                    id="draft-instructions"
                    value={draftInstructions}
                    onChange={(e) => setDraftInstructions(e.target.value)}
                    placeholder="Enter specific instructions for the drafting process (e.g., 'Focus on mechanical aspects', 'Use formal technical language', 'Include detailed process steps', etc.)..."
                    className="min-h-[120px] text-sm"
                    disabled={isProcessing}
                  />
                  <p className="text-xs text-gray-500">
                    Optional: Provide specific guidance on tone, focus areas, or formatting preferences for the draft output.
                  </p>
                </div>
              )}

              {/* Output Format Toggle — Draft tasks */}
              {['draft_background', 'draft_summary', 'draft_claims', 'draft_other_embodiments'].includes(task.id) && draftInputMode && (
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Output Format</Label>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant={(task.id === 'draft_background' ? draftBackgroundOutputFormat : draftOutputFormat) === 'sections' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => task.id === 'draft_background' ? setDraftBackgroundOutputFormat('sections') : setDraftOutputFormat('sections')}
                      className="flex-1"
                      disabled={isProcessing}
                    >
                      Sections (Structured)
                    </Button>
                    <Button
                      type="button"
                      variant={(task.id === 'draft_background' ? draftBackgroundOutputFormat : draftOutputFormat) === 'paragraphs' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => task.id === 'draft_background' ? setDraftBackgroundOutputFormat('paragraphs') : setDraftOutputFormat('paragraphs')}
                      className="flex-1"
                      disabled={isProcessing}
                    >
                      Full Paragraph(s)
                    </Button>
                  </div>
                  <p className="text-xs text-gray-500">
                    <strong>Sections</strong> returns structured fields for review. <strong>Full Paragraph(s)</strong> returns a ready-to-file prose draft section.
                  </p>
                </div>
              )}

              {/* Text Input */}
              {requiresTextInput() && task.id !== 'latex_to_docx' && task.id !== 'ocr_pdf' && task.id !== 'translate_document' && task.id !== 'antecedent_basis_check' && task.id !== 'technical_outline' && task.id !== 'parts_process_outline' && task.id !== 'custom_analysis' && task.id !== 'patent_drawing' && !task.id.includes('draft_') && (
                <div>
                  <Label htmlFor="text-input" className="text-sm font-medium mb-2 block">
                    {isTranslationTask() ? 'Text to Translate (or upload a file above)' :
                     requiresPatentSelection() ? 'Text Content (or select a patent above)' :
                     'Text Content / Instructions'}
                  </Label>
                  <Textarea
                    id="text-input"
                    value={textInput}
                    onChange={(e) => setTextInput(e.target.value)}
                    placeholder={getInputPlaceholder()}
                    className="min-h-[200px] font-mono text-sm"
                    disabled={isProcessing}
                  />
                  {error && (
                    <p className="text-sm text-red-600 mt-2">{error}</p>
                  )}
                </div>
              )}

              {/* Run Button */}
              <div>
                <div id="task-run-summary" className="mb-3 rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
                  <p className="font-medium text-slate-900">{task.name}</p>
                  <p className="mt-1 break-words">Input: {getInputSummary()}</p>
                  {task.id === 'claim_chart' && <p className="mt-1">Support citations: {includeSupport ? 'Included' : 'Not included'}</p>}
                  {task.id === 'translate_document' && <p className="mt-1">Translate to: {LANGUAGE_OPTIONS.find(language => language.value === targetLanguage)?.label || targetLanguage}</p>}
                  {task.id.includes('draft_') && <p className="mt-1">Output: {draftOutputFormat}</p>}
                  {task.id === 'oa_reporting_email' && <p className="mt-1">Template: {allEmailTemplates.find(template => template.id === selectedEmailTemplate)?.name || selectedEmailTemplate}</p>}
                  {inputError && <p className="mt-2 text-amber-800">{inputError}</p>}
                </div>
                {error && <p role="alert" className="mb-2 text-sm text-red-700">{error}</p>}
                <Button
                  onClick={handleRunTask}
                  disabled={isProcessing || task.coming_soon || !!inputError}
                  aria-describedby="task-run-summary"
                  className="w-full"
                  size="lg"
                >
                  {isProcessing ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Processing Task... {Math.floor(elapsedSeconds / 60)}:{String(elapsedSeconds % 60).padStart(2, '0')}
                    </>
                  ) : (
                    <>
                      <Play className="w-4 h-4 mr-2" />
                      {runTaskLabel}
                    </>
                  )}
                </Button>
                {task.coming_soon && (
                  <p className="text-sm text-yellow-700 mt-2">
                    This task is coming soon and not yet available.
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

      {/* Right: Results and Task History */}
      <div className="min-h-0 flex-1 xl:w-1/2 xl:flex-none flex flex-col bg-gray-50">
        {/* Results/History Tabs */}
        <div className="flex-shrink-0 border-b border-gray-200 bg-white px-6 py-3">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setActiveTab('result')}
              className={`text-sm font-medium pb-2 transition-colors ${
                activeTab === 'result'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Current Result
            </button>
            <button
              onClick={() => setActiveTab('history')}
              className={`text-sm font-medium pb-2 transition-colors ${
                activeTab === 'history'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Task History{!historyLoading && ` (${taskHistory.length})`}
            </button>
          </div>
        </div>

        {/* Content Display */}
        <ScrollArea className="flex-1">
          <div className="p-6 overflow-y-auto max-h-[calc(100vh-110px)]">
            {activeTab === 'result' ? (
              // Current Result Tab
              result ? (
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <FileText className="w-5 h-5 text-green-600" />
                      <h3 className="font-semibold text-lg text-gray-900">Task Result</h3>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        // Add the current result to chat attachments
                        handleAddToChat(
                          result,
                          task.name,
                          task.id,
                          result.timestamp
                        );
                      }}
                      className="gap-2"
                    >
                      <MessageSquarePlus className="w-4 h-4" />
                      Add to Chat
                    </Button>
                  </div>

                  {/* Task Metadata */}
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="font-medium text-gray-700">Completed:</span>
                        <div className="text-gray-900 mt-1">{formatTimestamp(result.timestamp || result.time_received)}</div>
                      </div>
                      {result.credits_used !== undefined && (
                        <div>
                          <span className="font-medium text-gray-700">Credits Used:</span>
                          <div className="text-gray-900 mt-1">{result.credits_used}</div>
                        </div>
                      )}
                      {(result.selected_number || result.document_id) && (
                        <div className="col-span-2">
                          <span className="font-medium text-gray-700">
                            {result.resource_type === 'office_action' ? 'Application:' : 'Document:'}
                          </span>
                          <div className="text-gray-900 mt-1 font-mono text-xs break-all">
                            {getDocumentDisplayName(
                              result.selected_number || result.document_id,
                              result.resource_type
                            )}
                          </div>
                        </div>
                      )}
                      {isViewableDocResult(result) && (
                        <div className="col-span-2">
                          <button
                            type="button"
                            className="inline-flex items-center gap-1.5 text-blue-600 hover:text-blue-800 text-sm font-medium hover:underline"
                            onClick={() => handleViewSpecification(result)}
                          >
                            <ExternalLink className="w-3.5 h-3.5" />
                            View Specification
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Render appropriate component based on task type */}
                  {(() => {
                    const parsedData = getParsedResult(getResultData(result));
                    const commonProps = {
                      result: parsedData,
                      theme: 'light' as const,
                      showHeader: true,
                      showToast,
                      onCopyResult: () => copyResult(parsedData, task.id),
                    };

                    switch (task.id) {
                      case 'parts_list':
                        return <PartsListDisplay {...commonProps} showToggle={true} />;
                      case 'spec_map':
                        return <SpecMapDisplay {...commonProps} showToggle={true} />;
                      case 'claims_support':
                        return <ClaimsSupportDisplay {...commonProps} showToggle={true} />;
                      case 'claim_chart':
                        return <ClaimChartDisplay {...commonProps} includeSupport={includeSupport} />;
                      case 'claims_with_numerals':
                        return <ClaimsWithNumeralsDisplay {...commonProps} showToggle={true} />;
                      case 'custom_analysis':
                        return <CustomAnalysisDisplay {...commonProps} showToggle={true} />;
                      case 'ir_metadata':
                        return <IRMetadataDisplay {...commonProps} />;
                      case 'ir_innovation':
                        return <IRInnovationAnalysisDisplay {...commonProps} />;
                      case 'ir_tech_class':
                        return <IRTechClassificationDisplay {...commonProps} />;
                      case 'draft_summary':
                        return <DraftSummaryDisplay {...commonProps} />;
                      case 'draft_detailed_description':
                        return <DraftDetailedDescriptionDisplay {...commonProps} />;
                      case 'draft_background':
                        return <DraftBackgroundDisplay {...commonProps} />;
                      case 'draft_claims':
                        return <DraftClaimsDisplay {...commonProps} />;
                      case 'draft_other_embodiments':
                        return <OtherEmbodimentsDisplay {...commonProps} />;
                      case 'oa_extract':
                        return <OAExtractDisplay {...commonProps} />;
                      case 'oa_analyze':
                        return <OAAnalyzeDisplay {...commonProps} />;
                      case 'oa_summary_art':
                        return <OASummaryArtDisplay {...commonProps} />;
                      case 'oa_timeline':
                        return <OATimelineDisplay {...commonProps} />;
                      case 'translate_document':
                        return <TranslateDocumentDisplay {...commonProps} />;
                      case 'latex_to_docx':
                        return <LaTeXConvertDisplay {...commonProps} />;
                      case 'ocr_pdf':
                        return <OCRPdfDisplay {...commonProps} />;
                      case 'antecedent_basis_check':
                        return <AntecedentBasisDisplay {...commonProps} />;
                      case 'technical_outline':
                        return <TechnicalOutlineDisplay {...commonProps} />;
                      case 'parts_process_outline':
                        return <PartsProcessOutlineDisplay {...commonProps} />;
                      case 'oa_reporting_email':
                        return <OAEmailResultView data={parsedData} />;
                      case 'prior_art_analysis':
                        return <PriorArtAnalysisDisplay {...commonProps} />;
                      case 'patent_drawing':
                        return <PatentDrawingDisplay {...commonProps} />;
                      default:
                        return (
                          <div className="p-4 rounded-lg bg-gray-50">
                            <pre className="text-xs overflow-auto whitespace-pre-wrap">
                              {JSON.stringify(parsedData, null, 2)}
                            </pre>
                          </div>
                        );
                    }
                  })()}
                </div>
              ) : isProcessing ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <Loader2 className="w-12 h-12 text-blue-500 animate-spin mb-4" />
                  <p className="text-sm text-gray-600">Processing your task...</p>
                  <p className="text-xs text-gray-500 mt-1">
                    Elapsed: {Math.floor(elapsedSeconds / 60)}:{String(elapsedSeconds % 60).padStart(2, '0')}
                  </p>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <FileText className="w-12 h-12 text-gray-300 mb-4" />
                  <p className="text-sm text-gray-600">No result yet</p>
                  <p className="text-xs text-gray-500 mt-1 max-w-sm">
                    Fill in the task details and select "{runTaskLabel}" to see results here
                  </p>
                </div>
              )
            ) : (
              // Task History Tab
              historyLoading ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 text-blue-500 animate-spin mb-3" />
                  <p className="text-sm text-gray-600">Loading history...</p>
                </div>
              ) : taskHistory.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <FileText className="w-12 h-12 text-gray-300 mb-4" />
                  <p className="text-sm text-gray-600">No history yet</p>
                  <p className="text-xs text-gray-500 mt-1 max-w-sm">
                    Run this task to start building your history
                  </p>
                </div>
              ) : selectedHistoryItem ? (
                // Display selected history item
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <FileText className="w-5 h-5 text-blue-600" />
                      <h3 className="font-semibold text-lg text-gray-900">Task Result</h3>
                    </div>
                    <div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleAddToChat(
                                                    selectedHistoryItem,
                                                    AVAILABLE_TASKS.find(t => t.id === selectedHistoryItem.task_type)?.name || 'Task Result',
                                                    selectedHistoryItem.task_type,
                                                    selectedHistoryItem.time_received || selectedHistoryItem.timestamp
                                                  )}
                        className="gap-2 mr-2"
                      >
                        <MessageSquarePlus className="w-4 h-4" />
                        Add to Chat
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setSelectedHistoryItem(null)}
                      >
                        <X className="w-4 h-4 mr-1" />
                        Close
                      </Button>
                    </div>
                  </div>

                  {/* Task Metadata */}
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="font-medium text-gray-700">Completed:</span>
                        <div className="text-gray-900 mt-1">{formatTimestamp(selectedHistoryItem.time_received || selectedHistoryItem.timestamp)}</div>
                      </div>
                      {selectedHistoryItem.credits_used !== undefined && (
                        <div>
                          <span className="font-medium text-gray-700">Credits Used:</span>
                          <div className="text-gray-900 mt-1">{selectedHistoryItem.credits_used}</div>
                        </div>
                      )}
                      {(selectedHistoryItem.selected_number || selectedHistoryItem.document_id) && (
                        <div className="col-span-2">
                          <span className="font-medium text-gray-700">
                            {selectedHistoryItem.resource_type === 'office_action' ? 'Application:' : 'Document:'}
                          </span>
                          <div className="text-gray-900 mt-1 font-mono text-xs break-all">
                            {getDocumentDisplayName(
                              selectedHistoryItem.selected_number || selectedHistoryItem.document_id,
                              selectedHistoryItem.resource_type
                            )}
                          </div>
                        </div>
                      )}
                      {isViewableDocResult(selectedHistoryItem) && (
                        <div className="col-span-2">
                          <button
                            type="button"
                            className="inline-flex items-center gap-1.5 text-blue-600 hover:text-blue-800 text-sm font-medium hover:underline"
                            onClick={() => handleViewSpecification(selectedHistoryItem)}
                          >
                            <ExternalLink className="w-3.5 h-3.5" />
                            View Specification
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Render appropriate component based on task type */}
                  {(() => {
                    const parsedData = getParsedResult(getResultData(selectedHistoryItem));
                    const commonProps = {
                      result: parsedData,
                      theme: 'light' as const,
                      showHeader: true,
                      showToast,
                      onCopyResult: () => copyResult(parsedData, selectedHistoryItem.task_type),
                    };

                    switch (selectedHistoryItem.task_type) {
                      case 'parts_list':
                        return <PartsListDisplay {...commonProps} showToggle={true} />;
                      case 'spec_map':
                        return <SpecMapDisplay {...commonProps} showToggle={true} />;
                      case 'claims_support':
                        return <ClaimsSupportDisplay {...commonProps} showToggle={true} />;
                      case 'claim_chart':
                        return <ClaimChartDisplay {...commonProps} includeSupport={includeSupport} />;
                      case 'claims_with_numerals':
                        return <ClaimsWithNumeralsDisplay {...commonProps} showToggle={true} />;
                      case 'custom_analysis':
                        return <CustomAnalysisDisplay {...commonProps} showToggle={true} />;
                      case 'ir_metadata':
                        return <IRMetadataDisplay {...commonProps} />;
                      case 'ir_innovation':
                        return <IRInnovationAnalysisDisplay {...commonProps} />;
                      case 'ir_tech_class':
                        return <IRTechClassificationDisplay {...commonProps} />;
                      case 'draft_summary':
                        return <DraftSummaryDisplay {...commonProps} />;
                      case 'draft_detailed_description':
                        return <DraftDetailedDescriptionDisplay {...commonProps} />;
                      case 'draft_background':
                        return <DraftBackgroundDisplay {...commonProps} />;
                      case 'draft_claims':
                        return <DraftClaimsDisplay {...commonProps} />;
                      case 'draft_other_embodiments':
                        return <OtherEmbodimentsDisplay {...commonProps} />;
                      case 'oa_extract':
                        return <OAExtractDisplay {...commonProps} />;
                      case 'oa_analyze':
                        return <OAAnalyzeDisplay {...commonProps} />;
                      case 'oa_summary_art':
                        return <OASummaryArtDisplay {...commonProps} />;
                      case 'oa_timeline':
                        return <OATimelineDisplay {...commonProps} />;
                      case 'translate_document':
                        return <TranslateDocumentDisplay {...commonProps} />;
                      case 'latex_to_docx':
                        return <LaTeXConvertDisplay {...commonProps} />;
                      case 'ocr_pdf':
                        return <OCRPdfDisplay {...commonProps} />;
                      case 'antecedent_basis_check':
                        return <AntecedentBasisDisplay {...commonProps} />;
                      case 'technical_outline':
                        return <TechnicalOutlineDisplay {...commonProps} />;
                      case 'parts_process_outline':
                        return <PartsProcessOutlineDisplay {...commonProps} />;
                      case 'oa_reporting_email':
                        return <OAEmailResultView data={parsedData} />;
                      case 'prior_art_analysis':
                        return <PriorArtAnalysisDisplay {...commonProps} />;
                      case 'patent_drawing':
                        return <PatentDrawingDisplay {...commonProps} />;
                      default:
                        return (
                          <div className="p-4 rounded-lg bg-gray-50">
                            <pre className="text-xs overflow-auto whitespace-pre-wrap">
                              {JSON.stringify(parsedData, null, 2)}
                            </pre>
                          </div>
                        );
                    }
                  })()}

                  <div className="mt-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleAddToChat(
                        selectedHistoryItem,
                        task.name,
                        selectedHistoryItem.task_type,
                        selectedHistoryItem.time_received || selectedHistoryItem.timestamp
                      )}
                      className="gap-2"
                    >
                      <MessageSquarePlus className="w-4 h-4" />
                      Add to Chat
                    </Button>
                  </div>
                </div>
              ) : (
                // List of history items
                <div className="space-y-2">
                  {taskHistory.map((item, index) => (
                    <button
                      key={item.analysis_id || index}
                      onClick={() => setSelectedHistoryItem(item)}
                      className="w-full text-left p-4 bg-white border border-gray-200 rounded-lg hover:border-blue-300 hover:shadow-sm transition-all"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {task.name}
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            {formatTimestamp(item.time_received || item.timestamp)}
                          </p>
                          {item.selected_number && (
                            <p className="text-xs text-gray-400 mt-1 truncate">
                              Document: {getDocumentDisplayName(item.selected_number, item.resource_type)}
                            </p>
                          )}
                        </div>
                        <div className="flex-shrink-0">
                          <Badge variant="outline" className="text-xs">
                            {item.credits_used || 0} credits
                          </Badge>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )
            )}
          </div>
        </ScrollArea>
      </div>

      {/* Email Template Manager Dialog */}
      <Dialog open={showTemplateManager} onOpenChange={(open) => {
        setShowTemplateManager(open);
        if (!open) {
          // Reload templates when dialog closes
          loadUserTemplates();
        }
      }}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden p-0">
          <EmailTemplateManager />
        </DialogContent>
      </Dialog>
    </div>
  );
}
